"use client";

import {
  Fragment,
  useState,
  useEffect,
  useMemo,
  useRef,
  useTransition,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import ModalPortal from "../ui/ModalPortal";
import Image from "next/image";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { setStoredStoreId } from "@/lib/store-storage";
import type { Store } from "@/lib/types/stores";
import { generateProductSlug } from "@shared/lib/slugify";
import { deleteCookie, getCookie, setCookie } from "@/lib/cookies";
import StoreSelector from "./StoreSelector";
import FulfillmentToggle from "./FulfillmentToggle";
import UserMenu from "./UserMenu";
import CartButton from "./CartButton";
import SearchBar from "./SearchBar";
import NavigationMenu from "./NavigationMenu";
import LocationModal from "./LocationModal";
import { useScrollLock } from "@/lib/use-scroll-lock";
import { usePersistentHeaderEnabled } from "./PersistentHeaderContext";
import DeliveryTruckIcon from "../icons/DeliveryTruckIcon";
import SearchIcon from "../icons/SearchIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import InfoIcon from "../icons/InfoIcon";
import CloseIcon from "../icons/CloseIcon";
import FlameFilledIcon from "../icons/FlameFilledIcon";
import GiftsIcon from "../icons/GiftsIcon";
import FlowerIcon from "../icons/FlowerIcon";
import PreRollIcon from "../icons/PreRollIcon";
import ExtractsIcon from "../icons/ExtractsIcon";
import VapesIcon from "../icons/VapesIcon";
import EdiblesIcon from "../icons/EdiblesIcon";
import TincturesIcon from "../icons/TincturesIcon";
import TopicalsIcon from "../icons/TopicalsIcon";
import AccessoriesIcon from "../icons/AccessoriesIcon";
import MerchIcon from "../icons/MerchIcon";
import StorefrontIcon from "../icons/StorefrontIcon";
import BurgerIcon from "../icons/BurgerIcon";
import UserIcon from "../icons/UserIcon";
import MarkerPinIcon from "../icons/MarkerPinIcon";
import { BellIcon, CartIcon, HeartIcon } from "../icons";
import { Button } from "@/components";
import {
  DANK_OPEN_DELIVERY_POSTAL_MODAL,
  DANK_OPEN_PICKUP_STORE_MODAL,
  DANK_NOTIFICATIONS_CHANGED,
  type NotificationsChangedDetail,
} from "@/lib/header-events";
import { hrefRequiresNativeAnchor } from "@/lib/hrefRequiresNativeAnchor";
import { toCategorySlug } from "@/app/search/url-utils";
import { getStorePath } from "@/lib/store-slug";

type SearchSuggestionProduct = {
  id: string;
  name: string;
  productSlug: string | null;
  image: string | null;
  brandName: string | null;
  classificationName: string | null;
  strain: string | null;
  price: number;
  inStock: number;
};

type SearchSuggestionsRecovery = {
  didYouMean: string | null;
  relatedCategories: string[];
  relatedBrands: string[];
  browseCollections: Array<{ title: string; handle: string }>;
};

type SearchSuggestions = {
  query: string;
  totalProducts: number;
  products: SearchSuggestionProduct[];
  collections: Array<{ id: string; title: string; handle: string }>;
  brands: Array<{ name: string }>;
  recovery?: SearchSuggestionsRecovery | null;
  appliedFilter?: { brand: string | null; collection: string | null };
};

function MegaSearchRecoveryLinks({
  recovery,
  variant,
  afterNavigate,
}: {
  recovery: SearchSuggestionsRecovery;
  variant: "desktop" | "mobile";
  afterNavigate?: () => void;
}) {
  const router = useRouter();
  const btn =
    variant === "mobile"
      ? "inline-flex items-center justify-center px-2.5 py-1.5 rounded-lg border border-neutral-10 bg-constant-white text-body-12-mobile text-text-secondary hover:border-blue-10 transition-colors select-none active:scale-95"
      : "inline-flex items-center justify-center px-3 py-2 rounded-xl border border-neutral-10 bg-constant-white text-body-12-desktop text-text-secondary hover:border-blue-10 transition-colors select-none active:scale-95";
  const labelCls =
    variant === "mobile" ? "text-body-11-mobile" : "text-body-12-desktop";

  const go = (href: string) => {
    router.push(href);
    afterNavigate?.();
  };

  return (
    <div
      className={`flex justify-center space-y-3 ${variant === "mobile" ? "pr-2" : ""}`}
    >
      {recovery.didYouMean ? (
        <Button raw
          type="button"
          className={`${btn} font-semibold text-blue-80 border-blue-10 bg-blue-5`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            go(`/search?q=${encodeURIComponent(recovery.didYouMean!)}`)
          }
        >
          Did you mean &quot;{recovery.didYouMean}&quot;?
        </Button>
      ) : null}

      {recovery.relatedCategories.length > 0 || recovery.relatedBrands.length > 0 ? (
        <div>
          <div className={`font-medium text-text-tertiary mb-2 ${labelCls}`}>
            Related categories &amp; brands
          </div>
          <div className="flex flex-wrap gap-2">
            {recovery.relatedCategories.map((c) => (
              <Button raw
                key={`zrc-${c}`}
                type="button"
                className={btn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  go(
                    `/search?category=${encodeURIComponent(toCategorySlug(c))}`
                  )
                }
              >
                {c}
              </Button>
            ))}
            {recovery.relatedBrands.map((b) => (
              <Button raw
                key={`zrb-${b}`}
                type="button"
                className={btn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => go(`/search?q=${encodeURIComponent(b)}`)}
              >
                {b}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {recovery.browseCollections.length > 0 ? (
        <div>
          <div className={`font-medium text-text-tertiary mb-2 ${labelCls}`}>
            Collections
          </div>
          <div className="flex flex-wrap gap-2">
            {recovery.browseCollections.slice(0, 6).map((c) => (
              <Button raw
                key={c.handle}
                type="button"
                className={btn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => go(`/collections/${encodeURIComponent(c.handle)}`)}
              >
                {c.title}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="pt-2">
        <Button raw
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => go("/collections")}
          className={
            variant === "mobile"
              ? "inline-flex items-center justify-center rounded-lg bg-blue-100 px-3.5 py-2 text-body-12-mobile font-semibold text-constant-white shadow-sm transition-colors active:scale-95"
              : "inline-flex items-center justify-center rounded-xl bg-blue-100 px-4 py-2 text-body-12-desktop font-semibold text-constant-white shadow-sm transition-colors active:scale-95"
          }
        >
          Browse all collections
          <svg
            aria-hidden
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ml-1.5"
          >
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
}

type MenuItem = {
  id: string;
  label: string;
  type: "collection" | "link" | "section" | "group";
  url?: string;
  collectionId?: string;
  collection?: {
    id: string;
    title: string;
    handle: string;
    bannerImageUrl?: string;
    description?: string;
  };
  icon?: string;
  imageUrl?: string;
  description?: string;
  column?: number;
  sortOrder: number;
  isVisible: boolean;
  isFeatured: boolean;
  badge?: string;
  badgeColor?: string;
  target?: string;
  children?: MenuItem[];
};

type FallbackGroup = { label: string; items: { id: string; label: string; href: string }[] };

const CATEGORY_FALLBACK_GROUPS: Record<string, FallbackGroup[]> = {
  // ─── Flowers: exact match to the image ───────────────────────────────────
  Flowers: [
    {
      label: 'Big bags', items: [
        { id: 'fb-fl-oz-best', label: 'Bestselling Ounces', href: '/search?category=Flower&sortBy=best-selling' },
        { id: 'fb-fl-oz-price', label: 'Ounces Under $100', href: '/search?category=Flower&priceMax=100' },
        { id: 'fb-fl-oz-thc', label: 'High THC Ounces (20%+)', href: '/search?category=Flower&thcMin=20' },
        { id: 'fb-fl-best', label: 'Bestselling Flower', href: '/search?category=Flower&sortBy=best-selling' },
      ]
    },
    {
      label: 'Shop by type', items: [
        { id: 'fb-fl-indica', label: 'Indica', href: '/search?category=Flower&strainType=Indica' },
        { id: 'fb-fl-sativa', label: 'Sativa', href: '/search?category=Flower&strainType=Sativa' },
        { id: 'fb-fl-hybrid', label: 'Hybrid', href: '/search?category=Flower&strainType=Hybrid' },
        { id: 'fb-fl-craft', label: 'Craft Cannabis', href: '/search?category=Flower' },
        { id: 'fb-fl-milled', label: 'Milled Flower', href: '/search?category=Flower' },
        { id: 'fb-fl-preroll', label: 'Pre-Rolls', href: '/search?category=Pre-Roll' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-fl-fresh', label: 'Fresh Drops', href: '/search?category=Flower&sortBy=fresh-drops' },
        { id: 'fb-fl-staff', label: 'Staff Picks', href: '/search?category=Flower&featured=true' },
        { id: 'fb-fl-discount', label: 'Discounted Flower', href: '/search?category=Flower&sortBy=price-asc' },
        { id: 'fb-fl-terpene', label: 'High Terpene (>3%)', href: '/search?category=Flower' },
      ]
    },
  ],

  // ─── Pre-Rolls ────────────────────────────────────────────────────────────
  'Pre-Rolls': [
    {
      label: 'Best packs', items: [
        { id: 'fb-pr-best', label: 'Bestselling Pre-Rolls', href: '/search?category=Pre-Roll&sortBy=best-selling' },
        { id: 'fb-pr-under30', label: 'Packs Under $30', href: '/search?category=Pre-Roll&priceMax=30' },
        { id: 'fb-pr-thc', label: 'High THC Pre-Rolls (20%+)', href: '/search?category=Pre-Roll&thcMin=20' },
        { id: 'fb-pr-singles', label: 'Singles Under $15', href: '/search?category=Pre-Roll&priceMax=15' },
      ]
    },
    {
      label: 'Shop by type', items: [
        { id: 'fb-pr-indica', label: 'Indica', href: '/search?category=Pre-Roll&strainType=Indica' },
        { id: 'fb-pr-sativa', label: 'Sativa', href: '/search?category=Pre-Roll&strainType=Sativa' },
        { id: 'fb-pr-hybrid', label: 'Hybrid', href: '/search?category=Pre-Roll&strainType=Hybrid' },
        { id: 'fb-pr-infused', label: 'Infused Pre-Rolls', href: '/search?category=Pre-Roll' },
        { id: 'fb-pr-blunts', label: 'Blunts', href: '/search?category=Pre-Roll' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-pr-fresh', label: 'Fresh Drops', href: '/search?category=Pre-Roll&sortBy=fresh-drops' },
        { id: 'fb-pr-staff', label: 'Staff Picks', href: '/search?category=Pre-Roll&featured=true' },
        { id: 'fb-pr-discount', label: 'Discounted Pre-Rolls', href: '/search?category=Pre-Roll&sortBy=price-asc' },
      ]
    },
  ],

  // ─── Extracts ─────────────────────────────────────────────────────────────
  Extracts: [
    {
      label: 'By format', items: [
        { id: 'fb-ex-shatter', label: 'Shatter', href: '/search?category=Extract' },
        { id: 'fb-ex-resin', label: 'Live Resin', href: '/search?category=Extract' },
        { id: 'fb-ex-wax', label: 'Budder & Wax', href: '/search?category=Extract' },
        { id: 'fb-ex-hash', label: 'Hash & Rosin', href: '/search?category=Extract' },
        { id: 'fb-ex-distillate', label: 'Distillate', href: '/search?category=Extract' },
      ]
    },
    {
      label: 'Shop by type', items: [
        { id: 'fb-ex-indica', label: 'Indica', href: '/search?category=Extract&strainType=Indica' },
        { id: 'fb-ex-sativa', label: 'Sativa', href: '/search?category=Extract&strainType=Sativa' },
        { id: 'fb-ex-hybrid', label: 'Hybrid', href: '/search?category=Extract&strainType=Hybrid' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-ex-fresh', label: 'Fresh Drops', href: '/search?category=Extract&sortBy=fresh-drops' },
        { id: 'fb-ex-staff', label: 'Staff Picks', href: '/search?category=Extract&featured=true' },
        { id: 'fb-ex-thc', label: 'Highest THC (80%+)', href: '/search?category=Extract&thcMin=80&sortBy=thc-desc' },
        { id: 'fb-ex-discount', label: 'Discounted Extracts', href: '/search?category=Extract&sortBy=price-asc' },
      ]
    },
  ],

  // ─── Edibles: Beverages is its own DB category; no strainType applies ─────
  Edibles: [
    {
      label: 'By format', items: [
        { id: 'fb-ed-gummies', label: 'Gummies', href: '/search?category=Edible' },
        { id: 'fb-ed-choc', label: 'Chocolates', href: '/search?category=Edible' },
        { id: 'fb-ed-bev', label: 'Beverages', href: '/search?category=Beverages' },
        { id: 'fb-ed-caps', label: 'Capsules', href: '/search?category=Edible' },
        { id: 'fb-ed-baked', label: 'Baked Goods', href: '/search?category=Edible' },
        { id: 'fb-ed-candy', label: 'Hard Candy', href: '/search?category=Edible' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-ed-fresh', label: 'Fresh Drops', href: '/search?category=Edible&sortBy=fresh-drops' },
        { id: 'fb-ed-staff', label: 'Staff Picks', href: '/search?category=Edible&featured=true' },
        { id: 'fb-ed-best', label: 'Best Sellers', href: '/search?category=Edible&sortBy=best-selling' },
        { id: 'fb-ed-discount', label: 'Discounted Edibles', href: '/search?category=Edible&sortBy=price-asc' },
      ]
    },
  ],

  // ─── Vaporizers ───────────────────────────────────────────────────────────
  Vaporizers: [
    {
      label: 'By format', items: [
        { id: 'fb-vp-510', label: '510 Cartridges', href: '/search?category=Vaporizer' },
        { id: 'fb-vp-disp', label: 'Disposable Vapes', href: '/search?category=Vaporizer' },
        { id: 'fb-vp-pods', label: 'Pod Systems', href: '/search?category=Vaporizer' },
        { id: 'fb-vp-batt', label: 'Batteries', href: '/search?category=Vaporizer' },
      ]
    },
    {
      label: 'Shop by type', items: [
        { id: 'fb-vp-indica', label: 'Indica', href: '/search?category=Vaporizer&strainType=Indica' },
        { id: 'fb-vp-sativa', label: 'Sativa', href: '/search?category=Vaporizer&strainType=Sativa' },
        { id: 'fb-vp-hybrid', label: 'Hybrid', href: '/search?category=Vaporizer&strainType=Hybrid' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-vp-fresh', label: 'Fresh Drops', href: '/search?category=Vaporizer&sortBy=fresh-drops' },
        { id: 'fb-vp-staff', label: 'Staff Picks', href: '/search?category=Vaporizer&featured=true' },
        { id: 'fb-vp-best', label: 'Best Sellers', href: '/search?category=Vaporizer&sortBy=best-selling' },
        { id: 'fb-vp-thc', label: 'Highest THC', href: '/search?category=Vaporizer&sortBy=thc-desc' },
      ]
    },
  ],

  // ─── Topicals: no strainType ──────────────────────────────────────────────
  Topicals: [
    {
      label: 'By format', items: [
        { id: 'fb-tp-cream', label: 'Creams & Lotions', href: '/search?category=Topical' },
        { id: 'fb-tp-balm', label: 'Balms', href: '/search?category=Topical' },
        { id: 'fb-tp-patch', label: 'Transdermal Patches', href: '/search?category=Topical' },
        { id: 'fb-tp-oil', label: 'Oils & Sprays', href: '/search?category=Topical' },
        { id: 'fb-tp-bath', label: 'Bath Products', href: '/search?category=Topical' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-tp-fresh', label: 'Fresh Drops', href: '/search?category=Topical&sortBy=fresh-drops' },
        { id: 'fb-tp-staff', label: 'Staff Picks', href: '/search?category=Topical&featured=true' },
        { id: 'fb-tp-best', label: 'Best Sellers', href: '/search?category=Topical&sortBy=best-selling' },
        { id: 'fb-tp-budget', label: 'Budget Picks', href: '/search?category=Topical&sortBy=price-asc' },
      ]
    },
  ],

  // ─── Tinctures ────────────────────────────────────────────────────────────
  Tinctures: [
    {
      label: 'By potency', items: [
        { id: 'fb-tn-cbd', label: 'CBD Tinctures', href: '/search?category=Tincture&sortBy=cbd-desc' },
        { id: 'fb-tn-thc', label: 'THC Tinctures', href: '/search?category=Tincture&sortBy=thc-desc' },
        { id: 'fb-tn-balanced', label: '1:1 Balanced', href: '/search?category=Tincture' },
        { id: 'fb-tn-highdose', label: 'High Dose (50mg+)', href: '/search?category=Tincture' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-tn-fresh', label: 'Fresh Drops', href: '/search?category=Tincture&sortBy=fresh-drops' },
        { id: 'fb-tn-staff', label: 'Staff Picks', href: '/search?category=Tincture&featured=true' },
        { id: 'fb-tn-best', label: 'Best Sellers', href: '/search?category=Tincture&sortBy=best-selling' },
        { id: 'fb-tn-budget', label: 'Budget Picks', href: '/search?category=Tincture&sortBy=price-asc' },
      ]
    },
  ],

  // ─── Accessories ──────────────────────────────────────────────────────────
  Accessories: [
    {
      label: 'Browse', items: [
        { id: 'fb-ac-papers', label: 'Papers & Wraps', href: '/search?category=Accessories' },
        { id: 'fb-ac-grinders', label: 'Grinders', href: '/search?category=Accessories' },
        { id: 'fb-ac-pipes', label: 'Pipes & Bongs', href: '/search?category=Accessories' },
        { id: 'fb-ac-storage', label: 'Storage', href: '/search?category=Accessories' },
        { id: 'fb-ac-rolling', label: 'Rolling Trays', href: '/search?category=Accessories' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-ac-fresh', label: 'New Arrivals', href: '/search?category=Accessories&sortBy=fresh-drops' },
        { id: 'fb-ac-staff', label: 'Staff Picks', href: '/search?category=Accessories&featured=true' },
        { id: 'fb-ac-best', label: 'Best Sellers', href: '/search?category=Accessories&sortBy=best-selling' },
        { id: 'fb-ac-budget', label: 'Budget Picks', href: '/search?category=Accessories&sortBy=price-asc' },
      ]
    },
  ],

  // ─── Dank Merch ───────────────────────────────────────────────────────────
  'Dank Merch': [
    {
      label: 'Browse', items: [
        { id: 'fb-dm-hoodies', label: 'Hoodies', href: '/search?category=Clothing' },
        { id: 'fb-dm-hats', label: 'Hats & Caps', href: '/search?category=Clothing' },
        { id: 'fb-dm-tees', label: 'T-Shirts', href: '/search?category=Clothing' },
        { id: 'fb-dm-acc', label: 'Accessories', href: '/search?category=Clothing' },
      ]
    },
    {
      label: 'Curated & deals', items: [
        { id: 'fb-dm-fresh', label: 'New Arrivals', href: '/search?category=Clothing&sortBy=fresh-drops' },
        { id: 'fb-dm-staff', label: 'Staff Picks', href: '/search?category=Clothing&featured=true' },
        { id: 'fb-dm-best', label: 'Best Sellers', href: '/search?category=Clothing&sortBy=best-selling' },
        { id: 'fb-dm-budget', label: 'Budget Picks', href: '/search?category=Clothing&sortBy=price-asc' },
      ]
    },
  ],
};

type Menu = {
  id: string;
  name: string;
  position: string | null;
  items: MenuItem[];
};

type HeaderProps = {
  stores: Store[];
  base: string;
  searchQuery?: string;
  persistentRoot?: boolean;
};

const ENTRY_PREFERENCES_KEY = "dank_entry_preferences";
const STORAGE_EXPIRY_DAYS = 30;
const MEGA_SEARCH_FILTER_COOKIE = "dank_mega_search_filter";

interface StoredPreferences {
  ageVerified: boolean;
  shoppingMethod: "pickup" | "delivery";
  selectedLocationId?: number;
  postalCode?: string;
  expiryDate: number;
}

function readEntryPreferences(): StoredPreferences | null {
  const cookieValue = getCookie(ENTRY_PREFERENCES_KEY);
  const legacyValue =
    !cookieValue && typeof localStorage !== "undefined"
      ? localStorage.getItem(ENTRY_PREFERENCES_KEY)
      : null;

  const raw = cookieValue || legacyValue;
  if (!raw) return null;

  try {
    const prefs: StoredPreferences = JSON.parse(raw);
    // one-time migration: move legacy localStorage to cookie and delete localStorage
    if (!cookieValue) {
      setCookie(ENTRY_PREFERENCES_KEY, JSON.stringify(prefs), {
        maxAgeDays: STORAGE_EXPIRY_DAYS,
      });
      try {
        localStorage.removeItem(ENTRY_PREFERENCES_KEY);
      } catch { }
    }
    return prefs;
  } catch {
    return null;
  }
}

function writeEntryPreferences(prefs: StoredPreferences): void {
  setCookie(ENTRY_PREFERENCES_KEY, JSON.stringify(prefs), {
    maxAgeDays: STORAGE_EXPIRY_DAYS,
  });
  // Cleanup legacy localStorage key
  try {
    localStorage.removeItem(ENTRY_PREFERENCES_KEY);
  } catch { }
}

function clearEntryPreferences(): void {
  deleteCookie(ENTRY_PREFERENCES_KEY);
  // Cleanup legacy localStorage key
  try {
    localStorage.removeItem(ENTRY_PREFERENCES_KEY);
  } catch { }
}

function MenuItemAnchor({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}) {
  if (hrefRequiresNativeAnchor(href)) {
    return (
      <a href={href.trim() || "#"} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

function HeaderContent({ stores, base, searchQuery = "" }: HeaderProps) {
  const router = useRouter();
  const [isStoreSwitchPending, startStoreSwitchTransition] = useTransition();
  const pathname = usePathname();
  const { state, setStore, setFulfillmentMode, openCart } = useCart();
  const { user, signOut } = useAuth();
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [megaSearchOpen, setMegaSearchOpen] = useState(false);
  const [megaSearchLoading, setMegaSearchLoading] = useState(false);
  const [megaSearchSuggestions, setMegaSearchSuggestions] =
    useState<SearchSuggestions | null>(null);
  const [megaSearchActiveIndex, setMegaSearchActiveIndex] = useState(-1);
  const [megaSearchSelectedChip, setMegaSearchSelectedChip] = useState<{
    type: "collection" | "brand";
    label: string;
    value: string;
  } | null>(null);
  const desktopMegaSearchRef = useRef<HTMLDivElement | null>(null);
  const mobileMegaSearchRef = useRef<HTMLDivElement | null>(null);
  const megaSearchAbortRef = useRef<AbortController | null>(null);
  const [headerMenu, setHeaderMenu] = useState<Menu | null>(null);
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Check if we're on a product page
  const isProductPage = pathname?.startsWith("/product/");

  // Sync searchInput when searchQuery prop changes
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const activeLocationId =
    state.storeCovaLocationId ||
    (stores.length > 0 ? stores[0].covaLocationId : null);

  const megaSearchChips = useMemo(() => {
    const chips: Array<{
      key: string;
      type: "collection" | "brand";
      label: string;
      value: string;
    }> = [];
    const q = searchInput.trim();
    if (!megaSearchSuggestions || !q) return chips;

    for (const c of megaSearchSuggestions.collections || []) {
      chips.push({
        key: `collection:${c.id}`,
        type: "collection",
        label: c.title,
        value: c.handle,
      });
    }

    for (const b of megaSearchSuggestions.brands || []) {
      chips.push({
        key: `brand:${b.name}`,
        type: "brand",
        label: b.name,
        value: b.name,
      });
    }

    // Deduplicate by label (case-insensitive)
    const seen = new Set<string>();
    return chips.filter((c) => {
      const k = c.label.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [megaSearchSuggestions, searchInput]);

  // Restore persisted chip selection (if any)
  useEffect(() => {
    try {
      const raw = getCookie(MEGA_SEARCH_FILTER_COOKIE);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        type?: "collection" | "brand";
        label?: string;
        value?: string;
      };
      if (
        parsed &&
        (parsed.type === "collection" || parsed.type === "brand") &&
        typeof parsed.value === "string" &&
        parsed.value.trim().length > 0
      ) {
        setMegaSearchSelectedChip({
          type: parsed.type,
          label: parsed.label || parsed.value,
          value: parsed.value,
        });
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist chip selection
  useEffect(() => {
    try {
      if (megaSearchSelectedChip) {
        setCookie(
          MEGA_SEARCH_FILTER_COOKIE,
          JSON.stringify(megaSearchSelectedChip),
          { maxAgeDays: 30 }
        );
      } else {
        deleteCookie(MEGA_SEARCH_FILTER_COOKIE);
      }
    } catch {
      // ignore
    }
  }, [megaSearchSelectedChip]);

  // Fetch mega header search suggestions (debounced)
  useEffect(() => {
    if (!megaSearchOpen) return;
    if (!activeLocationId) return;

    const q = searchInput.trim();
    if (q.length < 2) {
      setMegaSearchSuggestions(null);
      setMegaSearchLoading(false);
      setMegaSearchActiveIndex(-1);
      return;
    }

    setMegaSearchLoading(true);
    const controller = new AbortController();
    if (megaSearchAbortRef.current) {
      megaSearchAbortRef.current.abort();
    }
    megaSearchAbortRef.current = controller;

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.set("q", q);
        params.set("productsLimit", "6");
        params.set("chipsLimit", "50");
        if (megaSearchSelectedChip?.type === "brand") {
          params.set("brand", megaSearchSelectedChip.value);
        }
        if (megaSearchSelectedChip?.type === "collection") {
          params.set("collection", megaSearchSelectedChip.value);
        }
        const res = await fetch(
          `${base}/api/store/${activeLocationId}/search/suggestions?${params.toString()}`,
          { cache: "no-store", signal: controller.signal }
        );
        const json = await res.json();
        if (controller.signal.aborted) return;
        if (json?.success) {
          setMegaSearchSuggestions(json as SearchSuggestions);
          setMegaSearchActiveIndex(-1);
        } else {
          setMegaSearchSuggestions(null);
        }
      } catch {
        if (!controller.signal.aborted) {
          setMegaSearchSuggestions(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setMegaSearchLoading(false);
        }
      }
    }, 220);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    megaSearchOpen,
    searchInput,
    activeLocationId,
    base,
    megaSearchSelectedChip,
  ]);

  // Close mega search on outside click / escape
  useEffect(() => {
    if (!megaSearchOpen) return;

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const isInsideDesktop =
        !!desktopMegaSearchRef.current &&
        desktopMegaSearchRef.current.contains(target);
      const isInsideMobile =
        !!mobileMegaSearchRef.current &&
        mobileMegaSearchRef.current.contains(target);

      if (!isInsideDesktop && !isInsideMobile) {
        setMegaSearchOpen(false);
        setMegaSearchActiveIndex(-1);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMegaSearchOpen(false);
        setMegaSearchActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [megaSearchOpen]);

  const [headerMenuLoading, setHeaderMenuLoading] = useState(true);
  const [megaHeaderMenuLoading, setMegaHeaderMenuLoading] = useState(true);

  // Fetch header navigation menu
  useEffect(() => {
    setHeaderMenuLoading(true);
    (async () => {
      try {
        const res = await fetch(`${base}/api/navigation/menu/header`, {
          cache: "no-store",
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.success && data.menu) {
            setHeaderMenu(data.menu);
          }
        } else {
          // Only log in development
          if (process.env.NODE_ENV === "development") {
            console.warn(
              `Failed to load navigation menu: ${res.status} ${res.statusText}`
            );
          }
        }
      } catch (err) {
        // Silently handle network errors - backend may not be available
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "Failed to load navigation menu (backend may not be running):",
            err
          );
        }
      } finally {
        setHeaderMenuLoading(false);
      }
    })();
  }, [base]);

  // Fetch mega header navigation menu (secondary navigation bar)
  useEffect(() => {
    setMegaHeaderMenuLoading(true);
    (async () => {
      try {
        const res = await fetch(`${base}/api/navigation/menu/mega-header`, {
          cache: "no-store",
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.success && data.menu) {
            setMegaHeaderMenu(data.menu);
          } else {
            if (process.env.NODE_ENV === "development") {
              console.warn(
                '[Header] mega-header menu returned null. Configure Navigation Menu with identifier "mega-header".'
              );
            }
          }
        } else {
          // Only log in development
          if (process.env.NODE_ENV === "development") {
            console.warn(
              `Failed to load mega header menu: ${res.status} ${res.statusText}`
            );
          }
        }
      } catch (err) {
        // Silently handle network errors - backend may not be available
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "Failed to load mega header menu (backend may not be running):",
            err
          );
        }
      } finally {
        setMegaHeaderMenuLoading(false);
      }
    })();
  }, [base]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // Sync local mode state with cart context fulfillment mode
  const [mode, setMode] = useState<"pickup" | "delivery">(
    state.fulfillmentMode || "pickup"
  );
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    state.storeCovaLocationId
  );

  // Load stored preferences from EntryModal on mount and sync with CartContext
  useEffect(() => {
    const loadStoredPreferences = async () => {
      try {
        const prefs = readEntryPreferences();
        if (prefs) {
          // Check if preferences are still valid (not expired)
          if (prefs.expiryDate && Date.now() < prefs.expiryDate) {
            // Sync fulfillment mode
            if (
              prefs.shoppingMethod &&
              prefs.shoppingMethod !== state.fulfillmentMode
            ) {
              setFulfillmentMode(prefs.shoppingMethod);
              setMode(prefs.shoppingMethod);
            }

            // Sync location/store
            if (prefs.shoppingMethod === "pickup" && prefs.selectedLocationId) {
              if (prefs.selectedLocationId !== state.storeCovaLocationId) {
                setStore(prefs.selectedLocationId);
                setSelectedLocationId(prefs.selectedLocationId);
              }
            } else if (prefs.shoppingMethod === "delivery") {
              // For delivery, set the postal code in state
              if (prefs.postalCode) {
                setPostalCode(prefs.postalCode);
              }

              // If we have a stored location ID, use it directly
              if (prefs.selectedLocationId) {
                if (prefs.selectedLocationId !== state.storeCovaLocationId) {
                  setStore(prefs.selectedLocationId);
                  setSelectedLocationId(prefs.selectedLocationId);

                  // Find the store in the stores list
                  const foundStore = stores.find(
                    (s) => s.covaLocationId === prefs.selectedLocationId
                  );
                  if (foundStore) {
                    setResolvedStore(foundStore);
                  }
                } else {
                  // Location already matches, just update resolved store
                  const foundStore = stores.find(
                    (s) => s.covaLocationId === state.storeCovaLocationId
                  );
                  if (foundStore) {
                    setResolvedStore(foundStore);
                  }
                }
              } else if (prefs.postalCode && !state.storeCovaLocationId) {
                // No stored location ID, but we have postal code - try to resolve it
                try {
                  const postal = prefs.postalCode
                    .trim()
                    .toUpperCase()
                    .replace(/\s+/g, "");
                  const res = await fetch(
                    `${base}/api/locations/resolve?postal=${encodeURIComponent(
                      postal
                    )}`
                  );
                  const data = await res.json();

                  if (data?.success && data?.resolved?.covaLocationId) {
                    const resolvedLocationId = data.resolved.covaLocationId;
                    setStore(resolvedLocationId);
                    setSelectedLocationId(resolvedLocationId);

                    // Find the store in the stores list
                    const foundStore = stores.find(
                      (s) => s.covaLocationId === resolvedLocationId
                    );
                    if (foundStore) {
                      setResolvedStore(foundStore);
                    }
                  }
                } catch (error) {
                  console.error("Error resolving postal code on load:", error);
                }
              } else if (state.storeCovaLocationId) {
                // We already have a location in cart context, just update resolved store
                const foundStore = stores.find(
                  (s) => s.covaLocationId === state.storeCovaLocationId
                );
                if (foundStore) {
                  setResolvedStore(foundStore);
                }
              }
            }
          } else {
            // Expired, remove from storage
            clearEntryPreferences();
          }
        }
      } catch (e) {
        // Invalid storage, ignore
        console.error("Error loading stored preferences:", e);
      }
    };

    loadStoredPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep local mode in sync with cart context fulfillment mode
  useEffect(() => {
    if (state.fulfillmentMode) {
      setMode(state.fulfillmentMode);
    }
  }, [state.fulfillmentMode]);
  const [postalCode, setPostalCode] = useState("");
  const [postalError, setPostalError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  /** When true, closing the delivery postal modal without confirming should revert to Pickup (header toggle flow). */
  const deliveryModalOpenedFromPickupToggleRef = useRef(false);
  /** Set when “Select delivery menu” / comparison CTAs open the postal modal, after success, go to /search. */
  const navigateToSearchAfterPostalRef = useRef(false);
  /** Set when same CTAs open the pickup store list, after store pick, go to /search?fulfillment=pickup (skip full reload). */
  const navigateToPickupSearchAfterRef = useRef(false);
  const [newLocation, setNewLocation] = useState<Store | null>(null);
  const [currentLocation, setCurrentLocation] = useState<Store | null>(null);
  const [resolvedStore, setResolvedStore] = useState<Store | null>(null);
  const [showLocationsDropdown, setShowLocationsDropdown] = useState(false);
  const [showAlertBanner, setShowAlertBanner] = useState(() => {
    if (typeof window === 'undefined') return true;
    try { return localStorage.getItem('dank_alert_banner_dismissed') !== '1'; } catch { return true; }
  });
  /** "Read more" expands the shortened alert message inline (no navigation). */
  const [alertExpanded, setAlertExpanded] = useState(false);

  const [notifUnreadCount, setNotifUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) { setNotifUnreadCount(0); return; }
    const fetchUnread = async () => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
        if (!token) return;
        const res = await fetch(
          `${base}/api/auth/notifications?unreadOnly=true&limit=200`,
          { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }
        );
        if (res.ok) {
          const j = await res.json();
          if (j?.success && Array.isArray(j.notifications)) {
            setNotifUnreadCount(j.notifications.length);
          }
        }
      } catch { /* ignore */ }
    };
    void fetchUnread();
    const interval = setInterval(fetchUnread, 60_000);

    /* Live sync: when the /account notifications view marks something
       read / unread / refreshes, it dispatches DANK_NOTIFICATIONS_CHANGED
       with the authoritative count, update the bell badge immediately
       instead of waiting for the next 60s poll. */
    const onNotificationsChanged = (e: Event) => {
      const detail = (e as CustomEvent<NotificationsChangedDetail>).detail;
      if (detail && typeof detail.unreadCount === 'number') {
        setNotifUnreadCount(Math.max(0, detail.unreadCount));
      }
    };
    window.addEventListener(DANK_NOTIFICATIONS_CHANGED, onNotificationsChanged);

    return () => {
      clearInterval(interval);
      window.removeEventListener(DANK_NOTIFICATIONS_CHANGED, onNotificationsChanged);
    };
  }, [user, base]);

  const goToCheckoutForAuth = () => {
    router.push("/login");
  };
  const [megaHeaderMenu, setMegaHeaderMenu] = useState<Menu | null>(null);
  const [isShopMegaOpen, setIsShopMegaOpen] = useState(false);
  const [shopMegaHoverTimeout, setShopMegaHoverTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [activeShopCategory, setActiveShopCategory] =
    useState<string>("Flowers");
  const [hoveredMegaItemId, setHoveredMegaItemId] = useState<string | null>(
    null
  );
  const [megaHoverTimeout, setMegaHoverTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [storeHours, setStoreHours] = useState<{
    isOpen: boolean;
    closesAt?: string;
  } | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileSection, setExpandedMobileSection] = useState<
    string | null
  >(null);
  const [selectedMobileCategory, setSelectedMobileCategory] =
    useState<MenuItem | null>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const [clickedMobileMegaItem, setClickedMobileMegaItem] = useState<string | null>(null);
  /** Anchor coords for the portaled mobile mega-item dropdown.
   *  Captured from the chip's bounding rect at click time so the popover
   *  escapes the parent's overflow-x-auto clipping. */
  const [mobileMegaAnchor, setMobileMegaAnchor] = useState<{
    top: number;
    left: number;
    right: number;
    rightAlign: boolean;
  } | null>(null);

  // Find current location
  useEffect(() => {
    if (state.storeCovaLocationId) {
      const loc = stores.find(
        (s) => s.covaLocationId === state.storeCovaLocationId
      );
      if (loc) {
        setCurrentLocation(loc);
        // If in delivery mode, also update resolved store
        if (mode === "delivery") {
          setResolvedStore(loc);
        }
      }
    }
  }, [state.storeCovaLocationId, stores, mode]);

  // Update selected location when cart store changes
  useEffect(() => {
    setSelectedLocationId(state.storeCovaLocationId);
  }, [state.storeCovaLocationId]);

  useScrollLock(mobileMenuOpen);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setSelectedMobileCategory(null);
    setClickedMobileMegaItem(null);
  }, [pathname]);

  useEffect(() => {
    const openDeliveryPostalModal = () => {
      navigateToSearchAfterPostalRef.current = true;
      deliveryModalOpenedFromPickupToggleRef.current = false;
      setMode("delivery");
      setFulfillmentMode("delivery");
      setPostalError("");
      setShowLocationModal(true);
    };
    const openPickupStoreModal = () => {
      navigateToPickupSearchAfterRef.current = true;
      deliveryModalOpenedFromPickupToggleRef.current = false;
      setMode("pickup");
      setFulfillmentMode("pickup");
      setNewLocation(null);
      setPostalError("");
      setShowLocationModal(true);
    };
    window.addEventListener(DANK_OPEN_DELIVERY_POSTAL_MODAL, openDeliveryPostalModal);
    window.addEventListener(DANK_OPEN_PICKUP_STORE_MODAL, openPickupStoreModal);
    return () => {
      window.removeEventListener(DANK_OPEN_DELIVERY_POSTAL_MODAL, openDeliveryPostalModal);
      window.removeEventListener(DANK_OPEN_PICKUP_STORE_MODAL, openPickupStoreModal);
    };
  }, [setFulfillmentMode]);

  // Close mobile mega item dropdown when clicking outside
  useEffect(() => {
    if (!clickedMobileMegaItem) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && !(target as Element).closest('[data-mega-item]')) {
        setClickedMobileMegaItem(null);
        setMobileMegaAnchor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clickedMobileMegaItem]);

  const formatPostalCode = (value: string) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
  };

  /** Soft-refresh server components after store/location change (no full page reload). */
  const refreshAfterStoreChange = () => {
    startStoreSwitchTransition(() => {
      router.refresh();
    });
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPostalCode(e.target.value);
    setPostalCode(formatted);
    setPostalError("");

    // Save postal code to storage as user types (for delivery mode)
    if (mode === "delivery" && formatted.trim()) {
      try {
        const storedPrefs = readEntryPreferences();
        const prefs: StoredPreferences = storedPrefs || {
          ageVerified: true,
          shoppingMethod: "delivery",
          expiryDate: Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        };
        prefs.postalCode = formatted.trim();
        prefs.shoppingMethod = "delivery";
        prefs.expiryDate =
          Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        writeEntryPreferences(prefs);
      } catch (err) {
        // Silently fail - not critical
      }
    }
  };

  const handlePostalCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const postal = postalCode.trim().toUpperCase().replace(/\s+/g, "");

    if (!postal) {
      setPostalError("Please enter a postal code");
      return;
    }

    if (!/^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/.test(postal)) {
      setPostalError("Please enter a valid postal code (e.g., A1A 1A1)");
      return;
    }

    setLoading(true);
    setPostalError("");

    try {
      const res = await fetch(
        `${base}/api/locations/resolve?postal=${encodeURIComponent(postal)}`
      );
      const data = await res.json();

      if (data?.success && data?.resolved?.covaLocationId) {
        const resolvedLocationId = data.resolved.covaLocationId;
        const resolvedStoreName = data.resolved.storeName || "Store";

        // Find the store in the stores list
        const foundStore = stores.find(
          (s) => s.covaLocationId === resolvedLocationId
        );

        // Ensure fulfillment mode stays as delivery when submitting postal code
        // This is important because we're in delivery mode when the modal is open
        deliveryModalOpenedFromPickupToggleRef.current = false;
        setFulfillmentMode("delivery");
        setMode("delivery");

        if (foundStore) {
          // Set the store in cart context
          setStore(resolvedLocationId);
          setSelectedLocationId(resolvedLocationId);

          // Update resolved store state
          setResolvedStore(foundStore);

          // Save preferences to storage
          try {
            const storedPrefs = readEntryPreferences();
            const prefs: StoredPreferences = storedPrefs || {
              ageVerified: true,
              shoppingMethod: "delivery",
              expiryDate:
                Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
            };
            prefs.shoppingMethod = "delivery";
            prefs.selectedLocationId = resolvedLocationId;
            prefs.postalCode = postalCode.trim();
            prefs.expiryDate =
              Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
            writeEntryPreferences(prefs);
          } catch (err) {
            console.error("Failed to save preferences:", err);
          }

          // Close the modal
          setShowLocationModal(false);

          // Save store ID to localStorage
          setStoredStoreId(resolvedLocationId);

          setLoading(false);
          if (navigateToSearchAfterPostalRef.current) {
            navigateToSearchAfterPostalRef.current = false;
            router.push("/search");
          }
          // Store change no longer redirects - user stays on current page
        } else {
          // Store not found in list, but we have the location ID
          // Create a temporary store object
          const tempStore: Store = {
            covaLocationId: resolvedLocationId,
            name: resolvedStoreName,
          };

          setStore(resolvedLocationId);
          setSelectedLocationId(resolvedLocationId);
          setResolvedStore(tempStore);

          // Save preferences to storage
          try {
            const storedPrefs = readEntryPreferences();
            const prefs: StoredPreferences = storedPrefs || {
              ageVerified: true,
              shoppingMethod: "delivery",
              expiryDate:
                Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
            };
            prefs.shoppingMethod = "delivery";
            prefs.selectedLocationId = resolvedLocationId;
            prefs.postalCode = postalCode.trim();
            prefs.expiryDate =
              Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
            writeEntryPreferences(prefs);
          } catch (err) {
            console.error("Failed to save preferences:", err);
          }

          setShowLocationModal(false);
          // Save store ID to localStorage
          setStoredStoreId(resolvedLocationId);

          // Dispatch custom event for components to react to store change
          window.dispatchEvent(new CustomEvent('store-changed', { detail: { storeId: resolvedLocationId } }));

          setLoading(false);
          if (navigateToSearchAfterPostalRef.current) {
            navigateToSearchAfterPostalRef.current = false;
            router.push("/search");
          } else {
            refreshAfterStoreChange();
          }
        }
      } else {
        setPostalError(
          data?.message ||
          "No store found for that postal code. Please try another location."
        );
        setLoading(false);
      }
    } catch (err) {
      setPostalError("Unable to find store. Please try again.");
      setLoading(false);
    }
  };

  const handleLocationChange = (locationId: number) => {
    const isLocationDetailPage = pathname?.startsWith("/stores/");

    setStore(locationId);
    setSelectedLocationId(locationId);
    setShowLocationModal(false);
    setNewLocation(null);

    // Save store ID to localStorage
    setStoredStoreId(locationId);

    // Save preferences to storage
    try {
      const storedPrefs = readEntryPreferences();
      const prefs: StoredPreferences = storedPrefs || {
        ageVerified: true,
        shoppingMethod: mode,
        expiryDate: Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
      };
      prefs.shoppingMethod = mode;
      prefs.selectedLocationId = locationId;
      if (mode === "delivery" && postalCode) {
        prefs.postalCode = postalCode.trim();
      }
      prefs.expiryDate = Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      writeEntryPreferences(prefs);
    } catch (err) {
      console.error("Failed to save preferences:", err);
    }

    // Dispatch custom event for components to react to store change
    window.dispatchEvent(new CustomEvent('store-changed', { detail: { storeId: locationId } }));

    if (navigateToPickupSearchAfterRef.current) {
      navigateToPickupSearchAfterRef.current = false;
      router.push("/search?fulfillment=pickup");
      return;
    }

    if (isLocationDetailPage) {
      const selectedStore = stores.find((s) => s.covaLocationId === locationId);
      if (selectedStore) {
        router.push(getStorePath(selectedStore));
        return;
      }
    }

    refreshAfterStoreChange();
  };

  const handleLocationSelect = (locationId: number) => {
    const selectedStore = stores.find((s) => s.covaLocationId === locationId);
    if (selectedStore) {
      // If there's a current location and it's different, show modal
      if (currentLocation && currentLocation.covaLocationId !== locationId) {
        setNewLocation(selectedStore);
        setShowLocationModal(true);
      } else {
        handleLocationChange(locationId);
      }
    }
  };

  const handleLocationModalClose = () => {
    navigateToSearchAfterPostalRef.current = false;
    navigateToPickupSearchAfterRef.current = false;
    setShowLocationModal(false);
    setNewLocation(null);

    if (
      mode === "delivery" &&
      deliveryModalOpenedFromPickupToggleRef.current
    ) {
      deliveryModalOpenedFromPickupToggleRef.current = false;
      setPostalError("");
      setMode("pickup");
      setFulfillmentMode("pickup");

      try {
        const storedPrefs = readEntryPreferences();
        const prefs: StoredPreferences = storedPrefs || {
          ageVerified: true,
          shoppingMethod: "pickup",
          expiryDate: Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        };
        prefs.shoppingMethod = "pickup";
        if (selectedLocationId) {
          prefs.selectedLocationId = selectedLocationId;
        }
        prefs.expiryDate = Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        writeEntryPreferences(prefs);
      } catch (err) {
        console.error("Failed to save preferences:", err);
      }

      if (selectedLocationId) {
        const selectedStore = stores.find(
          (s) => s.covaLocationId === selectedLocationId
        );
        if (selectedStore) {
          setCurrentLocation(selectedStore);
        }
      }
    }
  };

  const handleModeChange = (newMode: "pickup" | "delivery") => {
    setMode(newMode);
    setFulfillmentMode(newMode);
    if (newMode === "delivery") {
      // Only clear postal code if switching from pickup to delivery
      // Keep resolved store if already set
      if (mode === "pickup") {
        deliveryModalOpenedFromPickupToggleRef.current = true;
        setPostalCode("");
        setResolvedStore(null);
      }
      setPostalError("");
      setShowLocationModal(true);
    } else {
      deliveryModalOpenedFromPickupToggleRef.current = false;
      setShowLocationModal(false);
      // When switching to pickup, use the current store selection
      if (selectedLocationId) {
        const selectedStore = stores.find(
          (s) => s.covaLocationId === selectedLocationId
        );
        if (selectedStore) {
          setCurrentLocation(selectedStore);
        }
      }
    }

    // Save preferences to storage
    try {
      const storedPrefs = readEntryPreferences();
      const prefs: StoredPreferences = storedPrefs || {
        ageVerified: true,
        shoppingMethod: newMode,
        expiryDate: Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
      };
      prefs.shoppingMethod = newMode;
      if (newMode === "pickup" && selectedLocationId) {
        prefs.selectedLocationId = selectedLocationId;
      } else if (newMode === "delivery" && postalCode) {
        prefs.postalCode = postalCode.trim();
      }
      prefs.expiryDate = Date.now() + STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      writeEntryPreferences(prefs);
    } catch (err) {
      console.error("Failed to save preferences:", err);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const locationId =
      selectedLocationId ||
      (stores.length > 0 ? stores[0].covaLocationId : null);
    if (locationId) {
      const params = new URLSearchParams();
      if (searchInput.trim()) {
        params.set("q", searchInput.trim());
      }
      router.push(`/search?${params.toString()}`);
      setSearchExpanded(false);
    }
  };

  const handleSearchClick = () => {
    setSearchExpanded(true);
  };

  const selectedStore = stores.find(
    (s) => s.covaLocationId === selectedLocationId
  );

  // Primary navigation items
  const primaryNavItems = (headerMenu?.items || [])
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const shopNavItem = primaryNavItems.find(
    (item) => (item.label || "").toLowerCase() === "shop"
  );
  const primaryNavItemsWithoutShop = primaryNavItems.filter(
    (item) => item.label?.toLowerCase() !== "shop"
  );

  // Mega header navigation items (secondary nav - product categories)
  const megaHeaderItems = (megaHeaderMenu?.items || [])
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  // Helper function to get href for menu item
  const getMenuItemHref = (item: MenuItem): string => {
    const resolveFirstHref = (node: MenuItem | undefined): string | null => {
      if (!node) return null;
      if (node.type === "collection" && node.collection?.handle) {
        return `/collections/${node.collection.handle}`;
      }
      if (node.url) return node.url;
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          const href = resolveFirstHref(child);
          if (href) return href;
        }
      }
      return null;
    };

    // For groups, link to the first usable descendant, otherwise fallback to search
    if (item.type === "group") {
      const href = resolveFirstHref(item);
      return href || `/search?category=${encodeURIComponent(item.label)}`;
    }
    if (item.type === "collection" && item.collection?.handle) {
      return `/collections/${item.collection.handle}`;
    }
    if (item.url) {
      return item.url;
    }
    return "#";
  };

  return (
    <>
      <header className="relative z-20 w-full min-w-0 max-w-full border-b border-neutral-10 bg-constant-white">
        {/* Top Marquee Banner, w-full/min-w-0/isolate keep wide marquee from clipping or overlapping the scrollbar */}
        <div className="relative isolate w-full min-w-0 max-w-full overflow-hidden bg-blue-100 py-1.5 text-constant-white md:py-2">
          {/* max-w-full + min-w-0: wide marquee tracks cannot widen the layout past the viewport (100% zoom overflow). */}
          <div className="flex w-full min-w-0 max-w-full">
            <div className="animate-marquee flex items-center gap-2 md:gap-4 whitespace-nowrap h-6">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 md:gap-2 px-2 md:px-4"
                >
                  <DeliveryTruckIcon
                    size={20}
                    color="#ffffff"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <span className="text-xs md:text-sm font-medium uppercase tracking-wide">
                    FREE 45 MIN CANNABIS DELIVERY
                  </span>
                </div>
              ))}
            </div>
            <div
              className="animate-marquee flex items-center gap-2 md:gap-4 whitespace-nowrap h-6"
              aria-hidden="true"
            >
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 md:gap-2 px-2 md:px-4"
                >
                  <DeliveryTruckIcon
                    size={20}
                    color="#ffffff"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <span className="text-xs md:text-sm font-medium uppercase tracking-wide">
                    FREE 45 MIN CANNABIS DELIVERY
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="bg-constant-white border-b border-neutral-10">
          <div className="mx-auto w-full">
            <div className="flex items-center justify-between h-20 md:h-24 gap-2 md:gap-4 py-3 md:py-4 px-4 sm:px-6 lg:px-10">
              {/* Left: menu then logo, as one group.
                  They were separate children of a justify-between row, so the
                  gap between them grew with the viewport and the logo drifted
                  toward the middle of the bar. The burger used to sit at the
                  far end of the row, past the cart, which put the way into the
                  site furthest from the thumb. */}
              <div className="flex min-w-0 items-center gap-2 h-full">
              <Button
                onClick={() => setMobileMenuOpen(true)}
                size="icon-md"
                variant="icon"
                color="white"
                className="lg:hidden shrink-0 rounded-lg"
                aria-label="Open menu"
              >
                <BurgerIcon size={24} className="w-6 h-6" color="currentColor" />
              </Button>

              <div className="flex items-center flex-shrink-0 h-full">
                <Link href="/" className="flex items-center gap-2 h-full">
                  <Image
                    src="/media/brand/logo.webp"
                    alt="Dank Cannabis"
                    width={109}
                    height={56}
                    priority
                    className="h-9 w-auto sm:h-[56px]"
                  />
                </Link>
              </div>
              </div>

              {/* Vertical Divider */}
              <div className="hidden lg:block h-full w-px bg-neutral-10"></div>

              {/* Center: Primary Navigation Links */}
              <nav className="hidden lg:flex items-center gap-6 relative flex-1 h-full">
                {/* Static Shop mega menu (uses mega-header data) */}
                {headerMenuLoading || megaHeaderMenuLoading ? (
                  <div className="relative h-full flex items-center">
                    <Button raw
                      type="button"
                      disabled
                      className="flex flex-row items-center gap-1 text-base font-medium text-blue-100 opacity-50"
                      aria-hidden="true"
                    >
                      <span className="w-12 h-5 bg-neutral-10 rounded animate-pulse" />
                      <ChevronDownIcon size={12} className="w-3 h-3 opacity-0 flex-shrink-0" />
                    </Button>
                  </div>
                ) : (
                  <div
                    className="relative h-full flex items-center"
                    onMouseEnter={() => {
                      if (shopMegaHoverTimeout) {
                        clearTimeout(shopMegaHoverTimeout);
                        setShopMegaHoverTimeout(null);
                      }
                      setIsShopMegaOpen(true);
                      const sourceItems =
                        shopNavItem?.children && shopNavItem.children.length > 0
                          ? shopNavItem.children
                          : megaHeaderItems;
                      const categoryItems = sourceItems.filter(
                        (i) => i.type === "group"
                      );
                      // Default active category to Flowers if present, otherwise first group
                      const fallback =
                        categoryItems.find((i) => i.label === "Flowers")?.label ||
                        categoryItems[0]?.label ||
                        "Flowers";
                      setActiveShopCategory((prev) => prev || fallback);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setIsShopMegaOpen(false);
                      }, 150);
                      setShopMegaHoverTimeout(timeout);
                    }}
                  >
                    <Button raw
                      type="button"
                      aria-haspopup="true"
                      aria-expanded={isShopMegaOpen}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === '' || e.key === 'ArrowDown') {
                          e.preventDefault();
                          setIsShopMegaOpen(true);
                        }
                        if (e.key === 'Escape') {
                          setIsShopMegaOpen(false);
                        }
                      }}
                      className={`flex flex-row items-center gap-1 text-base font-medium transition-colors ${isShopMegaOpen ? "text-blue-80" : "text-blue-100"
                        }`}
                    >
                      Shop
                      {isShopMegaOpen ? (
                        <ChevronUpIcon size={12} className="w-3 h-3 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon size={12} className="w-3 h-3 flex-shrink-0" />
                      )}
                    </Button>

                    {isShopMegaOpen && (
                      <div
                        className="absolute top-full left-0 -mt-1 bg-constant-white border border-neutral-10 rounded-2xl shadow-xl z-50 w-max max-w-[90vw] md:max-w-none min-h-[420px]"
                        onMouseEnter={() => {
                          if (shopMegaHoverTimeout) {
                            clearTimeout(shopMegaHoverTimeout);
                            setShopMegaHoverTimeout(null);
                          }
                          setIsShopMegaOpen(true);
                        }}
                        onMouseLeave={() => {
                          const timeout = setTimeout(() => {
                            setIsShopMegaOpen(false);
                          }, 150);
                          setShopMegaHoverTimeout(timeout);
                        }}
                      >
                        {(() => {
                          const sourceItems =
                            shopNavItem?.children &&
                              shopNavItem.children.length > 0
                              ? shopNavItem.children
                              : megaHeaderItems;

                          return sourceItems.length === 0 ? (
                            <div className="p-6">
                              <div className="text-blue-100 font-semibold">
                                Our shop is being updated
                              </div>
                              <div className="text-sm text-neutral-60 mt-2">
                                We&apos;re refreshing our product lineup. Please check back shortly &mdash; new items will be available soon.
                              </div>
                              <div className="mt-4">
                                <Button asChild variant="primary" size="md">
                                  <Link href="/">
                                    Back to Home
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          ) : (
                            (() => {
                              const categoryOrder = [
                                "Pre-Rolls",
                                "Flowers",
                                "Extracts",
                                "Topicals",
                                "Vaporizers",
                                "Edibles",
                                "Tinctures",
                                "Accessories",
                                "Dank Merch",
                              ];

                              const categoryItems = sourceItems
                                .filter((i) => i.type === "group")
                                .slice()
                                .sort((a, b) => {
                                  const ai = categoryOrder.indexOf(a.label || "");
                                  const bi = categoryOrder.indexOf(b.label || "");
                                  const aRank = ai === -1 ? 999 : ai;
                                  const bRank = bi === -1 ? 999 : bi;
                                  if (aRank !== bRank) return aRank - bRank;
                                  return (a.sortOrder || 0) - (b.sortOrder || 0);
                                });
                              const shopAllItem = sourceItems.find(
                                (i) =>
                                  i.type === "link" &&
                                  (i.label || "").toLowerCase() === "shop all"
                              );
                              const active =
                                categoryItems.find(
                                  (c) => c.label === activeShopCategory
                                ) || categoryItems[0];

                              const categoryIcon = (label: string) => {
                                const map: Record<string, any> = {
                                  "Pre-Rolls": PreRollIcon,
                                  Flowers: FlowerIcon,
                                  Extracts: ExtractsIcon,
                                  Vaporizers: VapesIcon,
                                  Vapes: VapesIcon,
                                  Edibles: EdiblesIcon,
                                  Tinctures: TincturesIcon,
                                  Topicals: TopicalsIcon,
                                  Accessories: AccessoriesIcon,
                                  "Dank Merch": MerchIcon,
                                };
                                return map[label] || null;
                              };

                              const activeChildren = (active?.children || [])
                                .slice()
                                .sort(
                                  (a, b) =>
                                    (a.sortOrder || 0) - (b.sortOrder || 0)
                                );
                              const promo = activeChildren.find(
                                (c) => !!c.imageUrl && c.type === "link"
                              );
                              const shopAll = activeChildren.find((c) =>
                                (c.label || "")
                                  .toLowerCase()
                                  .startsWith("shop all")
                              );

                              const columnGroups = activeChildren
                                .filter(
                                  (c) =>
                                    c.type === "group" &&
                                    Array.isArray(c.children) &&
                                    c.children.length > 0
                                )
                                .slice()
                                .sort(
                                  (a, b) =>
                                    (a.sortOrder || 0) - (b.sortOrder || 0)
                                );

                              const ctaHref = shopAll
                                ? getMenuItemHref(shopAll)
                                : getMenuItemHref(active);
                              const ctaLabel =
                                shopAll?.label ||
                                `Shop all ${active?.label || ""}`;
                              const fallbackGroups =
                                CATEGORY_FALLBACK_GROUPS[active?.label || ''] || [];

                              const groupCount = columnGroups.length > 0
                                ? Math.min(columnGroups.slice(0, 3).length, 3)
                                : fallbackGroups.length > 0
                                  ? Math.min(fallbackGroups.length, 3)
                                  : 1;
                              const gridColsClass = (
                                { 1: 'grid-cols-1', 2: 'grid-cols-1 sm:grid-cols-2', 3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' } as Record<number, string>
                              )[groupCount] ?? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

                              return (
                                <div className="flex flex-col md:flex-row h-full py-4">
                                  {/* Left: Categories */}
                                  <div className="w-full md:w-[220px] border-b md:border-b-0 md:border-r border-neutral-10 px-4 pb-4 md:pb-0">
                                    <div className="flex flex-col gap-0.5">
                                      {categoryItems.map((cat) => {
                                        const isActive = cat.label === active?.label;
                                        const IconComp = categoryIcon(cat.label);
                                        return (
                                          <Button raw
                                            key={cat.id}
                                            type="button"
                                            onMouseEnter={() => setActiveShopCategory(cat.label)}
                                            onFocus={() => setActiveShopCategory(cat.label)}
                                            className={`flex w-full flex-row items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[14px] font-medium transition-colors ${isActive
                                              ? "bg-blue-5 text-blue-100"
                                              : "text-text-primary"
                                              }`}
                                          >
                                            {IconComp ? (
                                              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center ${isActive ? "text-blue-100" : "text-neutral-60"}`}>
                                                <IconComp size={18} color="currentColor" />
                                              </span>
                                            ) : (
                                              <span className="h-5 w-5 flex-shrink-0 rounded bg-neutral-10" />
                                            )}
                                            <span className="flex-1 truncate">{cat.label}</span>
                                            {isActive && (
                                              <svg className="flex-shrink-0 text-blue-100" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9 18l6-6-6-6" />
                                              </svg>
                                            )}
                                          </Button>
                                        );
                                      })}
                                      {shopAllItem && (
                                        <>
                                          <hr className="my-2 border-neutral-10" />
                                          <MenuItemAnchor
                                            href={getMenuItemHref(shopAllItem)}
                                            className="flex w-full flex-row items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[14px] font-medium text-text-secondary transition-colors"
                                          >
                                            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-neutral-60">
                                              <StorefrontIcon size={18} color="currentColor" />
                                            </span>
                                            <span className="flex-1 truncate">{shopAllItem.label}</span>
                                          </MenuItemAnchor>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Middle + Right */}
                                  <div className="flex-1">
                                    <div className="flex h-full">
                                      {/* Middle: Columns */}
                                      <div className="flex-1 flex flex-col border-b md:border-b-0 md:border-r border-neutral-10 px-4 pb-4 md:pb-0">
                                        <div className={`grid ${gridColsClass} gap-4 md:gap-6 flex-1`}>
                                          {columnGroups.length > 0 ? (
                                            columnGroups
                                              .slice(0, 3)
                                              .map((group) => (
                                                <div key={group.id}>
                                                  <div className="mb-2.5 px-1 text-[13px] font-semibold text-blue-100">
                                                    {group.label}
                                                  </div>
                                                  <div className="flex flex-col">
                                                    {group
                                                      .children!.slice()
                                                      .sort(
                                                        (a, b) =>
                                                          (a.sortOrder || 0) -
                                                          (b.sortOrder || 0)
                                                      )
                                                      .map((child) => (
                                                        <MenuItemAnchor
                                                          key={child.id}
                                                          href={getMenuItemHref(child)}
                                                          className="flex items-center gap-1.5 px-1 py-1.5 text-[13px] text-text-secondary transition-colors"
                                                        >
                                                          <span>{child.label}</span>
                                                          {child.badge && (
                                                            <span
                                                              className={`text-[11px] px-1.5 py-0.5 rounded-md ${child.badgeColor === "red"
                                                                ? "bg-red-5 text-red-50"
                                                                : child.badgeColor === "green"
                                                                  ? "bg-green-5 text-green-50"
                                                                  : child.badgeColor === "blue"
                                                                    ? "bg-blue-5 text-blue-80"
                                                                    : "bg-neutral-5 text-neutral-60"
                                                                }`}
                                                            >
                                                              {child.badge}
                                                            </span>
                                                          )}
                                                        </MenuItemAnchor>
                                                      ))}
                                                  </div>
                                                </div>
                                              ))
                                          ) : fallbackGroups.length > 0 ? (
                                            fallbackGroups.map((group) => (
                                              <div key={group.label}>
                                                <div className="mb-2.5 px-1 text-[13px] font-semibold text-blue-100">
                                                  {group.label}
                                                </div>
                                                <div className="flex flex-col">
                                                  {group.items.map((item) => (
                                                    <MenuItemAnchor
                                                      key={item.id}
                                                      href={item.href}
                                                      className="px-1 py-1.5 text-[13px] text-text-secondary transition-colors"
                                                    >
                                                      {item.label}
                                                    </MenuItemAnchor>
                                                  ))}
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="col-span-3 text-sm text-neutral-60">
                                              No items configured for this category.
                                            </div>
                                          )}
                                        </div>

                                        <div className="mt-auto pt-6">
                                          <Button
                                            asChild
                                            variant="primary"
                                            size="lg"
                                            className="w-full !rounded-xl"
                                          >
                                            <MenuItemAnchor href={ctaHref}>
                                              {ctaLabel}
                                            </MenuItemAnchor>
                                          </Button>
                                        </div>
                                      </div>

                                      {/* Right: Promo */}
                                      <div className="w-full md:w-[352px] px-4 pt-4 md:pt-0">
                                        {promo ? (
                                          <div className="overflow-hidden h-full flex flex-col">
                                            <div className="relative w-full md:w-80 h-[210px] bg-neutral-10 rounded-xl">
                                              <img
                                                src={promo.imageUrl!}
                                                alt=""
                                                className="w-full h-full object-contain"
                                              />
                                            </div>
                                            <div className="flex flex-col flex-1 mt-4">
                                              <div className="text-text-primary font-medium text-lg">
                                                {promo.label}
                                              </div>
                                              {promo.description && (
                                                <div className="text-base text-text-secondary">
                                                  {promo.description}
                                                </div>
                                              )}
                                              <Button
                                                asChild
                                                variant="bordered"
                                                color="white"
                                                size="lg"
                                                className="mt-auto w-full !rounded-xl whitespace-nowrap"
                                              >
                                                <MenuItemAnchor href={promo.url || "#"}>
                                                  View best-selling
                                                </MenuItemAnchor>
                                              </Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="rounded-2xl border border-neutral-10 bg-bg-1 p-4 text-sm text-neutral-60 h-full">
                                            No promo configured for this category.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {headerMenuLoading ? (
                  <div className="flex items-center gap-6">
                    {[...Array(3)].map((_, i) => (
                      <Button raw
                        key={i}
                        type="button"
                        disabled
                        className="flex flex-row items-center gap-1 text-base font-medium text-blue-100 opacity-50"
                        aria-hidden="true"
                      >
                        <span className="w-16 h-5 bg-neutral-10 rounded animate-pulse" />
                      </Button>
                    ))}
                  </div>
                ) : (
                  <NavigationMenu items={primaryNavItemsWithoutShop} />
                )}
              </nav>

              {/* Right: Store Info, Pickup/Delivery Toggle, User, Cart */}
              <div className="flex items-center gap-1 md:gap-2 flex-shrink-0 h-full">
                <div className="hidden md:block">
                  <StoreSelector
                    stores={stores}
                    selectedLocationId={selectedLocationId}
                    mode={mode}
                    resolvedStore={resolvedStore}
                    postalCode={postalCode}
                    isPending={isStoreSwitchPending}
                    showLocationsDropdown={showLocationsDropdown}
                    onToggleDropdown={() =>
                      setShowLocationsDropdown(!showLocationsDropdown)
                    }
                    onLocationSelect={handleLocationSelect}
                    onShowLocationModal={() => {
                      deliveryModalOpenedFromPickupToggleRef.current = false;
                      setShowLocationModal(true);
                    }}
                  />
                </div>

                <div className="py-2 h-full">
                  <div
                    className="hidden lg:block h-full
                   w-px bg-neutral-10"
                  ></div>
                </div>

                <div className="hidden md:block">
                  <FulfillmentToggle
                    mode={mode}
                    onModeChange={handleModeChange}
                    disabled={isStoreSwitchPending}
                  />
                </div>
                {/* User Icon - Mobile */}
                <Button
                  onClick={goToCheckoutForAuth}
                  size="icon-md"
                  variant="icon"
                  color="white"
                  className="lg:hidden rounded-lg"
                  aria-label="User account"
                >
                  <UserIcon size={24} color="currentColor" />
                </Button>
                {/* Notification Bell - Mobile */}
                {user && (
                  <Button
                    asChild
                    size="icon-md"
                    variant="icon"
                    color="white"
                    className="lg:hidden relative rounded-lg"
                    aria-label="Notifications"
                  >
                    <Link href="/account?section=notifications">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      {notifUnreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none pointer-events-none">
                          {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                        </span>
                      )}
                    </Link>
                  </Button>
                )}
                {/* Favorites - Mobile */}
                <Button
                  asChild
                  size="icon-md"
                  variant="icon"
                  color="white"
                  className="lg:hidden rounded-lg"
                  aria-label="My Favorites"
                >
                  <Link href="/account?section=favorites" aria-label="My favorites">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                  </Link>
                </Button>
                {/* User Menu - Desktop */}
                <div className="hidden lg:block">
                  <UserMenu
                    user={user}
                    onShowAuthModal={goToCheckoutForAuth}
                  />
                </div>
                {/* Cart Button - Mobile with styled container */}
                <div className="lg:hidden">
                  <Button
                    onClick={openCart}
                    size="icon-md"
                    variant="icon"
                    color="white"
                    className="relative rounded-lg"
                    aria-label="Shopping cart"
                  >
                    <CartIcon size={24} color="currentColor" />
                    {(() => {
                      const itemCount = state.items.reduce(
                        (sum, item) => sum + item.quantity,
                        0
                      );
                      return itemCount > 0 ? (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-50 text-constant-white text-xs font-bold rounded-full flex items-center justify-center">
                          {itemCount > 9 ? "9+" : itemCount}
                        </span>
                      ) : null;
                    })()}
                  </Button>
                </div>
                {/* Favorites + Cart - Desktop */}
                <div className="hidden lg:flex items-center gap-2">
                  <Button
                    asChild
                    size="icon-md"
                    variant="icon"
                    color="white"
                    className="rounded-lg"
                    aria-label="My favorites"
                  >
                    <Link href="/account?section=favorites" aria-label="My favorites">
                      <HeartIcon size={24} color="currentColor" />
                    </Link>
                  </Button>
                  {/* Notification Bell - Desktop (only for logged-in users) */}
                  {user && (
                    <Button
                      asChild
                      size="icon-md"
                      variant="icon"
                      color="white"
                      className="relative rounded-lg"
                      aria-label="Notifications"
                    >
                      <Link href="/account?section=notifications">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        {notifUnreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none pointer-events-none">
                            {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                          </span>
                        )}
                      </Link>
                    </Button>
                  )}
                  <CartButton />
                </div>
              </div>
            </div>

            {/*
              Mobile search, in the header rather than in the drawer.

              This is the drawer's own search moved up: same suggestions,
              same keyboard handling, same product results as desktop. It
              used to be reachable only by opening the whole navigation
              drawer, so searching meant leaving the page you were on.
            */}
            <div className="lg:hidden relative px-4 pb-3 sm:px-6" ref={mobileMegaSearchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <SearchIcon
                  size={20}
                  className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-text-tertiary"
                />
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchInput}
                  onFocus={() => setMegaSearchOpen(true)}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setMegaSearchOpen(true);
                  }}
                  onKeyDown={(e) => {
                    const products = megaSearchSuggestions?.products || [];
                    if (e.key === "ArrowDown") {
                      if (products.length === 0) return;
                      e.preventDefault();
                      setMegaSearchOpen(true);
                      setMegaSearchActiveIndex((idx) =>
                        Math.min(idx + 1, products.length - 1)
                      );
                      return;
                    }
                    if (e.key === "ArrowUp") {
                      if (products.length === 0) return;
                      e.preventDefault();
                      setMegaSearchActiveIndex((idx) => Math.max(idx - 1, 0));
                      return;
                    }
                    if (
                      e.key === "Enter" &&
                      megaSearchOpen &&
                      megaSearchActiveIndex >= 0 &&
                      products[megaSearchActiveIndex]
                    ) {
                      e.preventDefault();
                      const p = products[megaSearchActiveIndex];
                      const productNameSlug = generateProductSlug({
                        name: p.name,
                        id: p.id,
                        brandName: p.brandName || undefined,
                        classificationName: p.classificationName || undefined,
                        sizeOrWeight: null,
                      });
                      const params = new URLSearchParams();
                      if (p.productSlug) params.set("variant", p.productSlug);
                      const qs = params.toString();
                      router.push(
                        `/product/${productNameSlug}${qs ? `?${qs}` : ""}`
                      );
                      setMegaSearchOpen(false);
                      setMegaSearchActiveIndex(-1);
                      setMobileMenuOpen(false);
                    }
                  }}
                  placeholder="Search flower, pre-rolls..."
                  className="h-12 w-full rounded-xl border border-neutral-10 bg-constant-white pl-11 pr-10 text-body-16 font-medium text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus:border-blue-80 focus:outline-none focus:ring-2 focus:ring-blue-80/20"
                />
                {searchInput.trim().length > 0 && (
                  <Button raw
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setMegaSearchSuggestions(null);
                      setMegaSearchActiveIndex(-1);
                      setMegaSearchSelectedChip(null);
                    }}
                    className="absolute inset-y-0 right-3 my-auto inline-flex h-8 w-8 transform-none items-center justify-center rounded-lg border border-neutral-10 bg-constant-white text-neutral-60 outline-none select-none hover:border-blue-10 focus:outline-none focus-visible:outline-none"
                    aria-label="Clear search"
                  >
                    <CloseIcon size={16} className="w-4 h-4" />
                  </Button>
                )}
              </form>

              {/* Mobile Search Suggestions Dropdown */}
              {megaSearchOpen && searchInput.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-4 mt-2 bg-constant-white overflow-hidden z-50 h-[calc(100vh-168px)] overflow-y-auto">
                  <div className="pl-4">
                    {(() => {
                      const products = megaSearchSuggestions?.products || [];
                      const total = megaSearchSuggestions?.totalProducts || 0;
                      const q = searchInput.trim();
                      const money = new Intl.NumberFormat("en-CA", {
                        style: "currency",
                        currency: "CAD",
                        minimumFractionDigits: 2,
                      });

                      return (
                        <div className="space-y-4">
                          {megaSearchChips.length > 0 && (
                            <div>
                              <div className="text-body-14-desktop font-medium text-text-tertiary mb-3">
                                Collections &amp; Brands
                              </div>
                              <div className="overflow-x-auto scrollbar-hide scroll-smooth">
                                <div className="flex flex-col gap-2 min-w-max">
                                  {/* First Row */}
                                  <div className="flex gap-2">
                                    {megaSearchChips
                                      .slice(
                                        0,
                                        Math.ceil(
                                          megaSearchChips.slice(0, 12).length / 2
                                        )
                                      )
                                      .map((chip) => (
                                        <Button raw
                                          key={chip.key}
                                          type="button"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => {
                                            const isSame =
                                              megaSearchSelectedChip &&
                                              megaSearchSelectedChip.type ===
                                              chip.type &&
                                              megaSearchSelectedChip.value ===
                                              chip.value;

                                            const nextChip = isSame
                                              ? null
                                              : {
                                                type: chip.type,
                                                label: chip.label,
                                                value: chip.value,
                                              };

                                            const normalized = (
                                              nextChip?.label || ""
                                            )
                                              .trim()
                                              .toLowerCase();
                                            const finalChip =
                                              normalized === "all products"
                                                ? null
                                                : nextChip;

                                            const params =
                                              new URLSearchParams();
                                            params.set("q", q);
                                            if (finalChip?.type === "brand") {
                                              params.set(
                                                "brand",
                                                finalChip.value
                                              );
                                            }
                                            if (finalChip?.type === "collection") {
                                              params.set(
                                                "collection",
                                                finalChip.value
                                              );
                                            }

                                            router.push(
                                              `/search?${params.toString()}`
                                            );
                                            setMegaSearchOpen(false);
                                            setMegaSearchActiveIndex(-1);
                                            setMegaSearchSelectedChip(finalChip);
                                            setMobileMenuOpen(false);
                                          }}
                                          className={`flex-shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-xl border text-body-14-mobile font-normal transition-all select-none active:scale-95 ${megaSearchSelectedChip &&
                                            megaSearchSelectedChip.type ===
                                            chip.type &&
                                            megaSearchSelectedChip.value ===
                                            chip.value
                                            ? "border-blue-10 bg-blue-5 text-blue-80"
                                            : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10"
                                            }`}
                                        >
                                          {chip.label}
                                        </Button>
                                      ))}
                                  </div>
                                  {/* Second Row */}
                                  <div className="flex gap-2">
                                    {megaSearchChips
                                      .slice(
                                        Math.ceil(
                                          megaSearchChips.slice(0, 12).length / 2
                                        ),
                                        12
                                      )
                                      .map((chip) => (
                                        <Button raw
                                          key={chip.key}
                                          type="button"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => {
                                            const isSame =
                                              megaSearchSelectedChip &&
                                              megaSearchSelectedChip.type ===
                                              chip.type &&
                                              megaSearchSelectedChip.value ===
                                              chip.value;

                                            const nextChip = isSame
                                              ? null
                                              : {
                                                type: chip.type,
                                                label: chip.label,
                                                value: chip.value,
                                              };

                                            const normalized = (
                                              nextChip?.label || ""
                                            )
                                              .trim()
                                              .toLowerCase();
                                            const finalChip =
                                              normalized === "all products"
                                                ? null
                                                : nextChip;

                                            const params =
                                              new URLSearchParams();
                                            params.set("q", q);
                                            if (finalChip?.type === "brand") {
                                              params.set(
                                                "brand",
                                                finalChip.value
                                              );
                                            }
                                            if (finalChip?.type === "collection") {
                                              params.set(
                                                "collection",
                                                finalChip.value
                                              );
                                            }

                                            router.push(
                                              `/search?${params.toString()}`
                                            );
                                            setMegaSearchOpen(false);
                                            setMegaSearchActiveIndex(-1);
                                            setMegaSearchSelectedChip(finalChip);
                                            setMobileMenuOpen(false);
                                          }}
                                          className={`flex-shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-xl border text-body-14-mobile font-normal transition-all select-none active:scale-95 ${megaSearchSelectedChip &&
                                            megaSearchSelectedChip.type ===
                                            chip.type &&
                                            megaSearchSelectedChip.value ===
                                            chip.value
                                            ? "border-blue-10 bg-blue-5 text-blue-80"
                                            : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10"
                                            }`}
                                        >
                                          {chip.label}
                                        </Button>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {megaSearchChips.length > 0 && (
                            <div className="border-t border-neutral-10" />
                          )}

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-body-14-desktop font-medium text-text-tertiary">
                                Products
                              </span>
                              {megaSearchLoading && (
                                <span className="text-body-12-mobile text-text-tertiary">
                                  Searching…
                                </span>
                              )}
                            </div>

                            {!megaSearchLoading && products.length === 0 ? (
                              <div className="py-9 text-center">
                                <div
                                  aria-hidden
                                  className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-5 text-blue-100 ring-1 ring-blue-10"
                                >
                                  <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <circle cx="11" cy="11" r="6.5" />
                                    <path d="m20 20-3.5-3.5" />
                                    <path d="M8.5 11h5" />
                                  </svg>
                                </div>
                                <p className="text-body-14-mobile font-semibold text-text-primary">
                                  No products match &quot;{q}&quot;
                                </p>
                                <p className="mx-auto mt-1.5 max-w-xs text-body-12-mobile text-text-tertiary">
                                  Try a different spelling, broader term, or pick a category.
                                </p>
                                {megaSearchSuggestions?.recovery ? (
                                  <MegaSearchRecoveryLinks
                                    recovery={megaSearchSuggestions.recovery}
                                    variant="mobile"
                                    afterNavigate={() => {
                                      setMegaSearchOpen(false);
                                      setMegaSearchActiveIndex(-1);
                                      setMobileMenuOpen(false);
                                    }}
                                  />
                                ) : null}
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {products.map((p, idx) => {
                                  const isActive = idx === megaSearchActiveIndex;
                                  const strain = p.strain || "";
                                  const category = p.classificationName || "";
                                  return (
                                    <Button raw
                                      key={`${p.id}:${idx}`}
                                      type="button"
                                      onMouseEnter={() =>
                                        setMegaSearchActiveIndex(idx)
                                      }
                                      onMouseDown={(e) => {
                                        // Prevent text selection but don't prevent click
                                        if (e.button === 0) {
                                          e.preventDefault();
                                        }
                                        e.stopPropagation();
                                      }}
                                      onTouchEnd={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        const productNameSlug =
                                          generateProductSlug({
                                            name: p.name,
                                            id: p.id,
                                            brandName: p.brandName || undefined,
                                            classificationName:
                                              p.classificationName || undefined,
                                            sizeOrWeight: null,
                                          });

                                        // Build the URL first
                                        let productUrl: string;
                                        if (!productNameSlug) {
                                          // Fallback to product ID if slug generation fails
                                          productUrl = `/product/${p.id}`;
                                        } else {
                                          const params = new URLSearchParams();
                                          if (p.productSlug) {
                                            params.set("variant", p.productSlug);
                                          }
                                          const qs = params.toString();
                                          productUrl = `/product/${productNameSlug}${qs ? `?${qs}` : ""
                                            }`;
                                        }

                                        // Navigate immediately before closing menu
                                        router.push(productUrl);

                                        // Close menus after navigation is initiated
                                        setMegaSearchOpen(false);
                                        setMegaSearchActiveIndex(-1);
                                        setMobileMenuOpen(false);
                                      }}
                                      className={`w-full rounded-xl border p-2 text-left transition-all select-none active:scale-[0.99] ${isActive
                                        ? "border-blue-15 bg-blue-5 shadow-sm"
                                        : "border-neutral-10 bg-constant-white hover:border-blue-15"
                                        }`}
                                    >
                                      <div className="flex items-center gap-[5px]">
                                        <div className="w-[52px] h-[52px] rounded-xl border border-neutral-10 bg-bg-1 overflow-hidden flex items-center justify-center flex-shrink-0">
                                          {p.image ? (
                                            <img
                                              src={p.image}
                                              alt={p.name}
                                              className="w-full h-full object-contain"
                                              loading="lazy"
                                            />
                                          ) : (
                                            <div className="w-full h-full bg-bg-2" />
                                          )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="text-body-14-mobile font-medium truncate text-text-primary">
                                            {p.name}
                                          </div>
                                          <div className="flex items-end justify-between gap-2 mt-1">
                                            <div className="flex flex-nowrap gap-1 overflow-x-auto scrollbar-hide min-w-0 flex-1">
                                              {strain && (
                                                <span
                                                  className={`flex-shrink-0 px-2 py-0.5 rounded-lg text-body-12-mobile font-normal ${isActive
                                                    ? "bg-blue-50 text-constant-white"
                                                    : "bg-bg-2 text-text-secondary"
                                                    }`}
                                                >
                                                  {strain}
                                                </span>
                                              )}
                                              {category && (
                                                <span
                                                  className={`flex-shrink-0 px-2 py-0.5 rounded-lg text-body-12-mobile font-normal ${isActive
                                                    ? "bg-blue-50 text-constant-white"
                                                    : "bg-bg-2 text-text-secondary"
                                                    }`}
                                                >
                                                  {category}
                                                </span>
                                              )}
                                            </div>
                                            <div
                                              className={`text-body-16-mobile font-bold whitespace-nowrap ${isActive
                                                ? "text-blue-80"
                                                : "text-text-primary"
                                                }`}
                                            >
                                              {money.format(p.price)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </Button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {total > 0 && (
                            <Button
                              variant="primary"
                              size="lg"
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                const params = new URLSearchParams();
                                params.set("q", q);
                                const normalizedLabel = (
                                  megaSearchSelectedChip?.label || ""
                                )
                                  .trim()
                                  .toLowerCase();
                                const chipToUse =
                                  normalizedLabel === "all products"
                                    ? null
                                    : megaSearchSelectedChip;
                                if (chipToUse?.type === "brand") {
                                  params.set("brand", chipToUse.value);
                                }
                                if (chipToUse?.type === "collection") {
                                  params.set(
                                    "collection",
                                    chipToUse.value
                                  );
                                }
                                router.push(`/search?${params.toString()}`);
                                setMegaSearchOpen(false);
                                setMegaSearchActiveIndex(-1);
                                setMobileMenuOpen(false);
                              }}
                              className="w-full rounded-xl"
                            >
                              <span className="flex items-center gap-1 justify-center">
                                See all {total} products
                                <svg
                                  aria-hidden
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="transition-transform duration-200 ease-out group-active:translate-x-0.5"
                                >
                                  <path d="M5 12h14M13 5l7 7-7 7" />
                                </svg>
                              </span>
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Secondary Navigation Bar - Categories and Search */}
        <div className="bg-constant-white border-b border-neutral-10">
          <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 py-3">
              {/* Mobile: Horizontal scrolling categories list */}
              <nav className="lg:hidden overflow-x-auto scrollbar-hide flex-1 -mx-4 sm:-mx-6 px-4 sm:px-6 max-sm:w-full">
                <div className="flex items-center gap-4 w-max">
                  {megaHeaderMenuLoading ? (
                    [...Array(4)].map((_, i) => (
                      <div key={i} className="flex-shrink-0">
                        <Button raw
                          type="button"
                          disabled
                          className="flex flex-row items-center gap-1 text-sm font-medium text-blue-100 opacity-50 whitespace-nowrap"
                          aria-hidden="true"
                        >
                          <span className="w-20 h-5 bg-neutral-10 rounded animate-pulse" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    megaHeaderItems
                      .filter(
                        (item) => (item.label || "").toLowerCase() !== "shop all"
                      )
                      .map((item, idx, arr) => {
                        const isGroup =
                          item.type === "group" &&
                          item.children &&
                          item.children.length > 0;
                        const isClicked = clickedMobileMegaItem === item.id;
                        // Right-align the portal popover for chips in the last two positions
                        // so it doesn't clip the viewport's right edge.
                        const shouldRightAlign = idx >= Math.max(0, arr.length - 2);
                        // Visually separate the "All products" entry from the category chips that follow.
                        const isAllProducts =
                          (item.label || "").toLowerCase().trim() === "all products";
                        return (
                          <Fragment key={item.id}>
                            <div className="relative flex-shrink-0" data-mega-item>
                              <Button raw
                                type="button"
                                onClick={(event) => {
                                  if (!isGroup) return;
                                  if (isClicked) {
                                    setClickedMobileMegaItem(null);
                                    setMobileMegaAnchor(null);
                                    return;
                                  }
                                  const rect = event.currentTarget.getBoundingClientRect();
                                  setMobileMegaAnchor({
                                    top: rect.bottom + 4,
                                    left: rect.left,
                                    right: window.innerWidth - rect.right,
                                    rightAlign: shouldRightAlign,
                                  });
                                  setClickedMobileMegaItem(item.id);
                                }}
                                className="flex flex-row items-center gap-1 text-sm font-medium text-blue-100 transition-colors whitespace-nowrap"
                              >
                                {item.label}
                                {isGroup && (
                                  <ChevronDownIcon
                                    size={12}
                                    className={`w-3 h-3 flex-shrink-0 transition-transform ${isClicked ? "rotate-180" : ""
                                      }`}
                                  />
                                )}
                              </Button>
                              {/* Dropdown is portaled to <body> so it escapes the parent's
                                overflow-x-auto (which would otherwise clip it). */}
                              {isGroup && isClicked && mobileMegaAnchor && typeof document !== "undefined"
                                ? createPortal(
                                  <div
                                    className="fixed bg-constant-white border border-neutral-10 rounded-xl shadow-lg z-[60] px-4 py-2.5 min-w-[200px] max-h-[60vh] overflow-y-auto"
                                    data-mega-item
                                    style={
                                      mobileMegaAnchor.rightAlign
                                        ? { top: mobileMegaAnchor.top, right: mobileMegaAnchor.right }
                                        : { top: mobileMegaAnchor.top, left: mobileMegaAnchor.left }
                                    }
                                  >
                                    <div className="space-y-0">
                                      {item.children
                                        ?.slice()
                                        .sort(
                                          (a, b) =>
                                            (a.sortOrder || 0) - (b.sortOrder || 0)
                                        )
                                        .map((child) => (
                                          <MenuItemAnchor
                                            key={child.id}
                                            href={getMenuItemHref(child)}
                                            className="block px-2 py-2 text-sm transition-colors rounded-lg text-blue-100 border hover:border-neutral-10 border-transparent"
                                          >
                                            <div className="flex items-center justify-between gap-2">
                                              <span>
                                                {child.type === "collection" &&
                                                  child.collection
                                                  ? child.collection.title
                                                  : child.label}
                                              </span>
                                              {child.badge && (
                                                <span
                                                  className={`text-xs px-1.5 py-0.5 rounded ${child.badgeColor === "red"
                                                    ? "bg-red-5 text-red-50"
                                                    : child.badgeColor === "green"
                                                      ? "bg-green-5 text-green-50"
                                                      : child.badgeColor === "blue"
                                                        ? "bg-blue-5 text-blue-80"
                                                        : "bg-neutral-5 text-neutral-60"
                                                    }`}
                                                >
                                                  {child.badge}
                                                </span>
                                              )}
                                            </div>
                                            {child.description && (
                                              <p className="text-xs text-neutral-60 mt-0.5">
                                                {child.description}
                                              </p>
                                            )}
                                          </MenuItemAnchor>
                                        ))}
                                    </div>
                                  </div>,
                                  document.body
                                )
                                : null}
                            </div>
                            {isAllProducts ? (
                              <div
                                aria-hidden
                                className="flex-shrink-0 self-stretch w-px bg-neutral-10"
                              />
                            ) : null}
                          </Fragment>
                        );
                      })
                  )}
                </div>
              </nav>

              {/* Left: Mega Header Navigation (configured in admin) - Desktop Only */}
              <nav className="hidden lg:flex items-center gap-6 flex-shrink-0">
                {megaHeaderMenuLoading ? (
                  [...Array(4)].map((_, i) => (
                    <div key={i} className="relative">
                      <Button raw
                        type="button"
                        disabled
                        className="flex flex-row items-center gap-1 text-base font-medium text-blue-100 opacity-50"
                        aria-hidden="true"
                      >
                        <span className="w-20 h-5 bg-neutral-10 rounded animate-pulse" />
                        <ChevronDownIcon size={12} className="w-3 h-3 opacity-0 flex-shrink-0" />
                      </Button>
                    </div>
                  ))
                ) : (
                  megaHeaderItems
                    .filter(
                      (item) => (item.label || "").toLowerCase() !== "shop all"
                    )
                    .map((item, idx, arr) => {
                      const isGroup =
                        item.type === "group" &&
                        item.children &&
                        item.children.length > 0;
                      const isHovered = hoveredMegaItemId === item.id;
                      const shouldRightAlign = idx >= Math.max(0, arr.length - 2);
                      return (
                        <div
                          key={item.id}
                          className="relative"
                          onMouseEnter={() => {
                            if (megaHoverTimeout) {
                              clearTimeout(megaHoverTimeout);
                              setMegaHoverTimeout(null);
                            }
                            if (isGroup) setHoveredMegaItemId(item.id);
                          }}
                          onMouseLeave={() => {
                            if (!isGroup) return;
                            const timeout = setTimeout(() => {
                              setHoveredMegaItemId(null);
                            }, 150);
                            setMegaHoverTimeout(timeout);
                          }}
                        >
                          <Button raw
                            type="button"
                            className={`flex flex-row items-center gap-1 text-base font-medium transition-colors ${isHovered ? "text-blue-80" : "text-blue-100"
                              }`}
                          >
                            {item.label}
                            {isGroup &&
                              (isHovered ? (
                                <ChevronUpIcon size={12} className="w-3 h-3 flex-shrink-0" />
                              ) : (
                                <ChevronDownIcon size={12} className="w-3 h-3 flex-shrink-0" />
                              ))}
                          </Button>

                          {isGroup && isHovered && (
                            <div
                              className={`absolute top-full ${shouldRightAlign ? "right-0" : "left-0"
                                } mt-1 bg-constant-white border border-neutral-10 rounded-xl shadow-lg z-50 px-4 py-2.5`}
                              onMouseEnter={() => {
                                if (megaHoverTimeout) {
                                  clearTimeout(megaHoverTimeout);
                                  setMegaHoverTimeout(null);
                                }
                                setHoveredMegaItemId(item.id);
                              }}
                              onMouseLeave={() => {
                                const timeout = setTimeout(() => {
                                  setHoveredMegaItemId(null);
                                }, 150);
                                setMegaHoverTimeout(timeout);
                              }}
                            >
                              <div className="space-y-0 w-max">
                                {item.description && (
                                  <div className="pt-1 pb-3">
                                    <div className="text-base text-text-secondary">
                                      {item.description}
                                    </div>
                                    <div className="mt-3 h-px bg-neutral-10" />
                                  </div>
                                )}
                                {item.children
                                  ?.slice()
                                  .sort(
                                    (a, b) =>
                                      (a.sortOrder || 0) - (b.sortOrder || 0)
                                  )
                                  .map((child) => (
                                    <MenuItemAnchor
                                      key={child.id}
                                      href={getMenuItemHref(child)}
                                      className="block px-2 py-2 text-base transition-colors rounded-lg text-blue-100 border hover:border-neutral-10 border-transparent"
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span>
                                          {child.type === "collection" &&
                                            child.collection
                                            ? child.collection.title
                                            : child.label}
                                        </span>
                                        {child.badge && (
                                          <span
                                            className={`text-xs px-1.5 py-0.5 rounded ${child.badgeColor === "red"
                                              ? "bg-red-5 text-red-50"
                                              : child.badgeColor === "green"
                                                ? "bg-green-5 text-green-50"
                                                : child.badgeColor === "blue"
                                                  ? "bg-blue-5 text-blue-80"
                                                  : "bg-neutral-5 text-neutral-60"
                                              }`}
                                          >
                                            {child.badge}
                                          </span>
                                        )}
                                      </div>
                                      {child.description && (
                                        <p className="text-xs text-neutral-60 mt-0.5">
                                          {child.description}
                                        </p>
                                      )}
                                    </MenuItemAnchor>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </nav>

              <div className="flex items-center justify-end gap-2 flex-1 max-sm:hidden">
                {/* Center: Search Bar - Hidden on mobile (shown in drawer) */}
                <div className="hidden md:flex flex-1 w-full md:max-w-[765px]">
                  <div ref={desktopMegaSearchRef} className="relative w-full">
                    <form
                      onSubmit={handleSearchSubmit}
                      className="relative w-full"
                    >
                      <SearchIcon
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-60 pointer-events-none"
                      />
                      <input
                        type="text"
                        value={searchInput}
                        onFocus={() => setMegaSearchOpen(true)}
                        onChange={(e) => {
                          setSearchInput(e.target.value);
                          setMegaSearchOpen(true);
                        }}
                        onKeyDown={(e) => {
                          const products =
                            megaSearchSuggestions?.products || [];
                          if (e.key === "ArrowDown") {
                            if (products.length === 0) return;
                            e.preventDefault();
                            setMegaSearchOpen(true);
                            setMegaSearchActiveIndex((idx) =>
                              Math.min(idx + 1, products.length - 1)
                            );
                            return;
                          }
                          if (e.key === "ArrowUp") {
                            if (products.length === 0) return;
                            e.preventDefault();
                            setMegaSearchActiveIndex((idx) =>
                              Math.max(idx - 1, 0)
                            );
                            return;
                          }
                          if (
                            e.key === "Enter" &&
                            megaSearchOpen &&
                            megaSearchActiveIndex >= 0 &&
                            products[megaSearchActiveIndex]
                          ) {
                            e.preventDefault();
                            const p = products[megaSearchActiveIndex];
                            const productNameSlug = generateProductSlug({
                              name: p.name,
                              id: p.id,
                              brandName: p.brandName || undefined,
                              classificationName:
                                p.classificationName || undefined,
                              sizeOrWeight: null,
                            });
                            const params = new URLSearchParams();
                            if (p.productSlug)
                              params.set("variant", p.productSlug);
                            const qs = params.toString();
                            if (!productNameSlug) {
                              router.push(`/product/${p.id}`);
                            } else {
                              router.push(
                                `/product/${productNameSlug}${qs ? `?${qs}` : ""}`
                              );
                            }
                            setMegaSearchOpen(false);
                            setMegaSearchActiveIndex(-1);
                          }
                        }}
                        placeholder="Search flower, pre-rolls, edibles"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-neutral-10 bg-constant-white text-body-16-desktop font-medium text-text-primary focus:border-blue-80 focus:outline-none focus:ring-2 focus:ring-blue-80/20"
                      />
                      {searchInput.trim().length > 0 && (
                        <Button raw
                          type="button"
                          onClick={() => {
                            setSearchInput("");
                            setMegaSearchSuggestions(null);
                            setMegaSearchActiveIndex(-1);
                            setMegaSearchSelectedChip(null);
                          }}
                          className="absolute inset-y-0 right-3 my-auto inline-flex h-8 w-8 transform-none items-center justify-center rounded-lg border border-neutral-10 bg-constant-white text-neutral-60 outline-none select-none hover:border-blue-10 focus:outline-none focus-visible:outline-none"
                          aria-label="Clear search"
                        >
                          <CloseIcon size={16} className="w-4 h-4" />
                        </Button>
                      )}
                    </form>

                    {megaSearchOpen && searchInput.trim().length >= 2 && (
                      <div className="absolute top-full mt-2 left-0 w-full rounded-2xl border border-neutral-10 bg-constant-white shadow-xl overflow-hidden z-30">
                        <div className="p-4">
                          {(() => {
                            const products =
                              megaSearchSuggestions?.products || [];
                            const total =
                              megaSearchSuggestions?.totalProducts || 0;
                            const q = searchInput.trim();
                            const money = new Intl.NumberFormat("en-CA", {
                              style: "currency",
                              currency: "CAD",
                              minimumFractionDigits: 2,
                            });

                            const half = Math.ceil(products.length / 2);
                            const left = products.slice(0, half);
                            const right = products.slice(half);

                            return (
                              <div className="space-y-4">
                                {megaSearchChips.length > 0 && (
                                  <div>
                                    <div className="text-body-14-desktop font-medium text-text-tertiary mb-3">
                                      Collections &amp; Brands
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {megaSearchChips
                                        .slice(0, 12)
                                        .map((chip) => (
                                          <Button raw
                                            key={chip.key}
                                            type="button"
                                            onMouseDown={(e) =>
                                              e.preventDefault()
                                            }
                                            onClick={() => {
                                              const isSame =
                                                megaSearchSelectedChip &&
                                                megaSearchSelectedChip.type ===
                                                chip.type &&
                                                megaSearchSelectedChip.value ===
                                                chip.value;

                                              const nextChip = isSame
                                                ? null
                                                : {
                                                  type: chip.type,
                                                  label: chip.label,
                                                  value: chip.value,
                                                };

                                              // "All Products" should behave like no filter.
                                              const normalized = (
                                                nextChip?.label || ""
                                              )
                                                .trim()
                                                .toLowerCase();
                                              const finalChip =
                                                normalized === "all products"
                                                  ? null
                                                  : nextChip;

                                              const params =
                                                new URLSearchParams();
                                              params.set("q", q);
                                              if (finalChip?.type === "brand") {
                                                params.set(
                                                  "brand",
                                                  finalChip.value
                                                );
                                              }
                                              if (
                                                finalChip?.type ===
                                                "collection"
                                              ) {
                                                params.set(
                                                  "collection",
                                                  finalChip.value
                                                );
                                              }

                                              router.push(
                                                `/search?${params.toString()}`
                                              );
                                              setMegaSearchOpen(false);
                                              setMegaSearchActiveIndex(-1);
                                              setMegaSearchSelectedChip(
                                                finalChip
                                              );
                                              setMobileMenuOpen(false);
                                            }}
                                            className={`inline-flex items-center justify-center px-3 py-2 rounded-xl border text-body-14-desktop font-medium transition-all select-none active:scale-95 ${megaSearchSelectedChip &&
                                              megaSearchSelectedChip.type ===
                                              chip.type &&
                                              megaSearchSelectedChip.value ===
                                              chip.value
                                              ? "border-blue-10 bg-blue-5 text-blue-80"
                                              : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10"
                                              }`}
                                          >
                                            {chip.label}
                                          </Button>
                                        ))}
                                    </div>
                                  </div>
                                )}

                                {megaSearchChips.length > 0 && (
                                  <div className="border-t border-neutral-10" />
                                )}

                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-body-14-desktop font-medium text-text-tertiary">
                                      Products
                                    </span>
                                    {megaSearchLoading && (
                                      <span className="text-body-12-desktop text-text-tertiary">
                                        Searching…
                                      </span>
                                    )}
                                  </div>

                                  {!megaSearchLoading &&
                                    products.length === 0 ? (
                                    <div className="py-10 text-center">
                                      <div
                                        aria-hidden
                                        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-5 text-blue-100 ring-1 ring-blue-10"
                                      >
                                        <svg
                                          width="26"
                                          height="26"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.75"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <circle cx="11" cy="11" r="6.5" />
                                          <path d="m20 20-3.5-3.5" />
                                          <path d="M8.5 11h5" />
                                        </svg>
                                      </div>
                                      <p className="text-body-14-desktop font-semibold text-text-primary">
                                        No products match &quot;{q}&quot;
                                      </p>
                                      <p className="mx-auto mt-1.5 max-w-sm text-body-12-desktop text-text-tertiary">
                                        Try a different spelling, broader term, or pick a category below.
                                      </p>
                                      {megaSearchSuggestions?.recovery ? (
                                        <MegaSearchRecoveryLinks
                                          recovery={megaSearchSuggestions.recovery}
                                          variant="desktop"
                                          afterNavigate={() => {
                                            setMegaSearchOpen(false);
                                            setMegaSearchActiveIndex(-1);
                                          }}
                                        />
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2">
                                      <div className="space-y-2 md:pr-2">
                                        {left.map((p, idx) => {
                                          const globalIdx = idx;
                                          const isActive =
                                            globalIdx === megaSearchActiveIndex;
                                          const strain = p.strain || "";
                                          const category =
                                            p.classificationName || "";
                                          return (
                                            <Button raw
                                              key={`${p.id}:${globalIdx}`}
                                              type="button"
                                              onMouseEnter={() =>
                                                setMegaSearchActiveIndex(
                                                  globalIdx
                                                )
                                              }
                                              onMouseDown={(e) =>
                                                e.preventDefault()
                                              }
                                              onClick={() => {
                                                const productNameSlug =
                                                  generateProductSlug({
                                                    name: p.name,
                                                    id: p.id,
                                                    brandName:
                                                      p.brandName || undefined,
                                                    classificationName:
                                                      p.classificationName ||
                                                      undefined,
                                                    sizeOrWeight: null,
                                                  });
                                                const params =
                                                  new URLSearchParams();
                                                if (p.productSlug) {
                                                  params.set(
                                                    "variant",
                                                    p.productSlug
                                                  );
                                                }
                                                const qs = params.toString();
                                                if (!productNameSlug) {
                                                  router.push(`/product/${p.id}`);
                                                } else {
                                                  router.push(
                                                    `/product/${productNameSlug}${qs ? `?${qs}` : ""
                                                    }`
                                                  );
                                                }
                                                setMegaSearchOpen(false);
                                                setMegaSearchActiveIndex(-1);
                                              }}
                                              className={`w-full rounded-xl border p-2 text-left transition-colors duration-200 ease-out select-none ${isActive
                                                ? "border-blue-15 bg-blue-5"
                                                : "border-neutral-10 bg-constant-white hover:border-blue-15 hover:bg-blue-5"
                                                }`}
                                            >
                                              <div className="flex items-center gap-3">
                                                <div className="w-14 h-14 rounded-xl border border-neutral-10 bg-bg-1 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                  {p.image ? (
                                                    <img
                                                      src={p.image}
                                                      alt={p.name}
                                                      className="w-full h-full object-contain"
                                                      loading="lazy"
                                                    />
                                                  ) : (
                                                    <div className="w-full h-full bg-bg-2" />
                                                  )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                  <div
                                                    className={`text-body-16-desktop font-medium truncate ${isActive ? "text-blue-100" : "text-text-primary"
                                                      }`}
                                                  >
                                                    {p.name}
                                                  </div>
                                                  <div className="flex items-end justify-between gap-2 mt-1">
                                                    <div className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide min-w-0 flex-1">
                                                      {strain && (
                                                        <span
                                                          className="flex-shrink-0 px-2 py-0.5 rounded-md text-body-12-desktop font-medium bg-bg-2 text-text-secondary"
                                                        >
                                                          {strain}
                                                        </span>
                                                      )}
                                                      {category && (
                                                        <span
                                                          className="flex-shrink-0 px-2 py-0.5 rounded-md text-body-12-desktop font-medium bg-bg-2 text-text-secondary"
                                                        >
                                                          {category}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div
                                                      className={`text-body-16-desktop font-bold whitespace-nowrap ${isActive ? "text-blue-100" : "text-text-primary"
                                                        }`}
                                                    >
                                                      {money.format(p.price)}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </Button>
                                          );
                                        })}
                                      </div>

                                      <div className="space-y-2 md:pl-2 md:border-l md:border-neutral-10">
                                        {right.map((p, idx) => {
                                          const globalIdx = half + idx;
                                          const isActive =
                                            globalIdx === megaSearchActiveIndex;
                                          const strain = p.strain || "";
                                          const category =
                                            p.classificationName || "";
                                          return (
                                            <Button raw
                                              key={`${p.id}:${globalIdx}`}
                                              type="button"
                                              onMouseEnter={() =>
                                                setMegaSearchActiveIndex(
                                                  globalIdx
                                                )
                                              }
                                              onMouseDown={(e) =>
                                                e.preventDefault()
                                              }
                                              onClick={() => {
                                                const productNameSlug =
                                                  generateProductSlug({
                                                    name: p.name,
                                                    id: p.id,
                                                    brandName:
                                                      p.brandName || undefined,
                                                    classificationName:
                                                      p.classificationName ||
                                                      undefined,
                                                    sizeOrWeight: null,
                                                  });
                                                const params =
                                                  new URLSearchParams();
                                                if (p.productSlug) {
                                                  params.set(
                                                    "variant",
                                                    p.productSlug
                                                  );
                                                }
                                                const qs = params.toString();
                                                if (!productNameSlug) {
                                                  router.push(`/product/${p.id}`);
                                                } else {
                                                  router.push(
                                                    `/product/${productNameSlug}${qs ? `?${qs}` : ""
                                                    }`
                                                  );
                                                }
                                                setMegaSearchOpen(false);
                                                setMegaSearchActiveIndex(-1);
                                              }}
                                              className={`w-full rounded-xl border p-2 text-left transition-colors duration-200 ease-out select-none ${isActive
                                                ? "border-blue-15 bg-blue-5"
                                                : "border-neutral-10 bg-constant-white hover:border-blue-15 hover:bg-blue-5"
                                                }`}
                                            >
                                              <div className="flex items-center gap-3">
                                                <div className="w-14 h-14 rounded-xl border border-neutral-10 bg-bg-1 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                  {p.image ? (
                                                    <img
                                                      src={p.image}
                                                      alt={p.name}
                                                      className="w-full h-full object-contain"
                                                      loading="lazy"
                                                    />
                                                  ) : (
                                                    <div className="w-full h-full bg-bg-2" />
                                                  )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                  <div
                                                    className={`text-body-16-desktop font-medium truncate ${isActive ? "text-blue-100" : "text-text-primary"
                                                      }`}
                                                  >
                                                    {p.name}
                                                  </div>
                                                  <div className="flex items-end justify-between gap-2 mt-1">
                                                    <div className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide min-w-0 flex-1">
                                                      {strain && (
                                                        <span
                                                          className="flex-shrink-0 px-2 py-0.5 rounded-md text-body-12-desktop font-medium bg-bg-2 text-text-secondary"
                                                        >
                                                          {strain}
                                                        </span>
                                                      )}
                                                      {category && (
                                                        <span
                                                          className="flex-shrink-0 px-2 py-0.5 rounded-md text-body-12-desktop font-medium bg-bg-2 text-text-secondary"
                                                        >
                                                          {category}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div
                                                      className={`text-body-16-desktop font-bold whitespace-nowrap ${isActive ? "text-blue-100" : "text-text-primary"
                                                        }`}
                                                    >
                                                      {money.format(p.price)}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </Button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {total > 0 && (
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="lg"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      const params = new URLSearchParams();
                                      params.set("q", q);
                                      const normalizedLabel = (
                                        megaSearchSelectedChip?.label ||
                                        ""
                                      )
                                        .trim()
                                        .toLowerCase();
                                      const chipToUse =
                                        normalizedLabel === "all products"
                                          ? null
                                          : megaSearchSelectedChip;
                                      if (chipToUse?.type === "brand") {
                                        params.set("brand", chipToUse.value);
                                      }
                                      if (chipToUse?.type === "collection") {
                                        params.set(
                                          "collection",
                                          chipToUse.value
                                        );
                                      }
                                      router.push(
                                        `/search?${params.toString()}`
                                      );
                                      setMegaSearchOpen(false);
                                      setMegaSearchActiveIndex(-1);
                                    }}
                                    className="w-full rounded-xl"
                                  >
                                    <span className="flex items-center gap-1 justify-center">
                                      See all {total} products
                                      <svg
                                        aria-hidden
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="transition-transform duration-200 ease-out"
                                      >
                                        <path d="M5 12h14M13 5l7 7-7 7" />
                                      </svg>
                                    </span>
                                  </Button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Gift Icon - Hidden on mobile.
                    Had no handler and no href, so it was a button that did
                    nothing. Goes to the gift cards page. */}
                <Button
                  asChild
                  size="icon-md"
                  variant="icon"
                  color="white"
                  className="hidden md:flex rounded-lg"
                  aria-label="Gift cards"
                  title="Gift cards"
                >
                  <Link href="/gift-cards">
                    <GiftsIcon size={24} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Banner (always reserve space to prevent header CLS) */}

        {showAlertBanner && (
          <div
            className={`relative min-h-[46px] sm:min-h-[36px] z-20 ${showAlertBanner ? "bg-yellow-50" : "bg-transparent"
              }`}
          >
            <div className="mx-auto flex w-full max-w-[1440px] items-start justify-between gap-2 px-4 py-1.5 sm:items-center sm:px-6 lg:px-10">
              <div className="flex w-full items-start gap-1.5 text-blue-80 sm:items-center sm:justify-center">
                <InfoIcon size={20} className="mt-px h-5 w-5 shrink-0 text-neutral-70 sm:mt-0" />
                {/* Desktop: full message always fits */}
                <p className="hidden text-body-14-desktop font-medium leading-relaxed sm:block">
                  Due to the strike, orders shipped via Canada Post may be delayed. Alternative
                  shipping methods also remain available and undisrupted where applicable.
                </p>

                {/* Mobile: shortened message; "Read more" expands it inline (no navigation) */}
                <p className="text-body-12-mobile font-medium leading-relaxed sm:hidden">
                  {alertExpanded
                    ? 'Due to the strike, orders shipped via Canada Post may be delayed. Alternative shipping methods also remain available and undisrupted where applicable.'
                    : 'Due to the strike, orders shipped via Canada Post may be delayed. Alternative shipping…'}{' '}
                  <Button
                    raw
                    type="button"
                    onClick={() => setAlertExpanded((v) => !v)}
                    className="font-bold underline underline-offset-2"
                  >
                    {alertExpanded ? 'Read less' : 'Read more'}
                  </Button>
                </p>
              </div>
              <Button raw
                type="button"
                onClick={() => {
                  setShowAlertBanner(false);
                  try { localStorage.setItem('dank_alert_banner_dismissed', '1'); } catch { }
                }}
                className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-lg font-semibold leading-none text-blue-80 transition-colors active:scale-[0.98]"
                aria-label="Dismiss alert"
              >
                <span className="select-none" aria-hidden="true">
                  ×
                </span>
              </Button>
            </div>
          </div>
        )}
      </header>

      <LocationModal
        isOpen={showLocationModal}
        mode={mode}
        postalCode={postalCode}
        postalError={postalError}
        loading={loading}
        currentLocation={currentLocation}
        newLocation={newLocation}
        stores={stores}
        selectedLocationId={selectedLocationId}
        onClose={handleLocationModalClose}
        onPostalCodeChange={handlePostalCodeChange}
        onPostalCodeSubmit={handlePostalCodeSubmit}
        onLocationChange={handleLocationChange}
        onUseLocation={async () => {
          setPostalError(
            "Location services not fully implemented. Please enter postal code manually."
          );
        }}
        onGeolocationError={(error) => {
          if (error.code === 1) {
            setPostalError(
              "Location access denied. Please enter postal code manually."
            );
          } else {
            setPostalError(
              "Unable to get your location. Please enter postal code manually."
            );
          }
        }}
      />


      {/* Mobile Navigation Drawer */}
      <ModalPortal>
        <div
          className={`lg:hidden ${mobileMenuOpen ? "" : "pointer-events-none"}`}
        >
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-[#07063CBF] z-[1000] transition-opacity duration-300 ${mobileMenuOpen ? "opacity-100" : "opacity-0"
              }`}
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div
            className={`fixed inset-y-0 left-0 w-full max-w-80 bg-constant-white z-[1000] lg:hidden flex flex-col shadow-xl transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
              }`}
          >
            {/* Header */}
            <div className="p-4">
              <Link
                href="/"
                className="flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Image
                  src="/media/brand/logo.webp"
                  alt="Dank Cannabis"
                  width={109}
                  height={56}
                  className="h-[56px] w-auto"
                />
              </Link>
            </div>

            {/* Navigation Sections */}
            <div className="h-auto px-4 overflow-y-auto">
              {/* Shop Section */}
              {(() => {
                const sourceItems =
                  shopNavItem?.children && shopNavItem.children.length > 0
                    ? shopNavItem.children
                    : megaHeaderItems;
                const categoryItems = sourceItems
                  .filter((i) => i.type === "group")
                  .slice()
                  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                const shopAllItem = sourceItems.find(
                  (i) =>
                    i.type === "link" &&
                    (i.label || "").toLowerCase() === "shop all"
                );

                const categoryIcon = (label: string) => {
                  const map: Record<string, any> = {
                    "Pre-Rolls": PreRollIcon,
                    Flowers: FlowerIcon,
                    Extracts: ExtractsIcon,
                    Vaporizers: VapesIcon,
                    Vapes: VapesIcon,
                    Edibles: EdiblesIcon,
                    Tinctures: TincturesIcon,
                    Topicals: TopicalsIcon,
                    Accessories: AccessoriesIcon,
                    "Dank Merch": MerchIcon,
                  };
                  return map[label] || null;
                };

                return (
                  <div>
                    <Button raw
                      type="button"
                      onClick={() =>
                        setExpandedMobileSection(
                          expandedMobileSection === "shop" ? null : "shop"
                        )
                      }
                      className="w-full flex items-center justify-between px-1 py-4 text-base font-semibold text-text-primary transition-colors border-t border-neutral-10 rounded-none"
                    >
                      <span>Shop</span>
                      {expandedMobileSection === "shop" ? (
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-10">
                          <ChevronUpIcon size={14} className="text-text-secondary" />
                        </div>
                      ) : (
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-10">
                          <ChevronDownIcon size={14} className="text-text-secondary" />
                        </div>
                      )}
                    </Button>
                    {expandedMobileSection === "shop" && (
                      <div className="pb-4">
                        {selectedMobileCategory ? (
                          // Show subcollections for selected category
                          (() => {
                            const activeChildren = (
                              selectedMobileCategory?.children || []
                            )
                              .slice()
                              .sort(
                                (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
                              );

                            const columnGroups = activeChildren
                              .filter(
                                (c) =>
                                  c.type === "group" &&
                                  Array.isArray(c.children) &&
                                  c.children.length > 0
                              )
                              .slice()
                              .sort(
                                (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
                              );

                            const shopAll = activeChildren.find((c) =>
                              (c.label || "").toLowerCase().startsWith("shop all")
                            );

                            const ctaHref = shopAll
                              ? getMenuItemHref(shopAll)
                              : getMenuItemHref(selectedMobileCategory);
                            const ctaLabel =
                              shopAll?.label ||
                              `Shop all ${selectedMobileCategory?.label || ""}`;

                            return (
                              <div className="space-y-4 pt-2">
                                {/* Back button */}
                                <Button raw
                                  type="button"
                                  onClick={() => setSelectedMobileCategory(null)}
                                  className="flex items-center gap-2 text-sm font-medium text-text-secondary transition-colors"
                                >
                                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-10">
                                    <ChevronLeftIcon size={14} className="text-text-secondary" />
                                  </div>
                                  <span>Back to all products</span>
                                </Button>

                                {/* Column Groups (Type, Strain, Effect) */}
                                {columnGroups.length > 0 ? (
                                  <div className="grid grid-cols-2 gap-1 pl-2">
                                    {columnGroups.map((group) => (
                                      <div key={group.id}>
                                        <h3 className="text-sm font-medium text-text-primary mb-1">
                                          {group.label}
                                        </h3>
                                        <div>
                                          {group
                                            .children!.slice()
                                            .sort(
                                              (a, b) =>
                                                (a.sortOrder || 0) -
                                                (b.sortOrder || 0)
                                            )
                                            .map((child) => (
                                              <MenuItemAnchor
                                                key={child.id}
                                                href={getMenuItemHref(child)}
                                                onClick={() =>
                                                  setMobileMenuOpen(false)
                                                }
                                                className="block py-2 text-sm font-normal text-text-secondary transition-colors"
                                              >
                                                {child.type === "collection" &&
                                                  child.collection
                                                  ? child.collection.title
                                                  : child.label}
                                              </MenuItemAnchor>
                                            ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-neutral-60">
                                    No subcollections configured for this
                                    category.
                                  </div>
                                )}

                                {/* Shop all button */}
                                <MenuItemAnchor
                                  href={ctaHref}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="block w-full rounded-xl border border-blue-80 bg-blue-80 text-constant-white py-3 text-center text-sm font-medium transition-colors hover:border-neutral-70"
                                >
                                  {ctaLabel}
                                </MenuItemAnchor>
                              </div>
                            );
                          })()
                        ) : (
                          // Show category list
                          <div className="grid grid-cols-2 gap-1">
                            {categoryItems.map((cat) => {
                              const IconComp = categoryIcon(cat.label || "");
                              return (
                                <Button raw
                                  key={cat.id}
                                  type="button"
                                  onClick={() => setSelectedMobileCategory(cat)}
                                  className="flex items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium text-text-secondary transition-colors"
                                >
                                  {IconComp && <IconComp size={16} color="currentColor" className="flex-shrink-0 text-neutral-40" />}
                                  <span>{cat.label}</span>
                                </Button>
                              );
                            })}
                            {shopAllItem && (
                              <MenuItemAnchor
                                href={getMenuItemHref(shopAllItem)}
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium text-text-secondary transition-colors"
                              >
                                <span>{shopAllItem.label}</span>
                              </MenuItemAnchor>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Other Navigation Items */}
              {primaryNavItemsWithoutShop.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedMobileSection === item.id;
                const href = !hasChildren ? getMenuItemHref(item) : undefined;

                return (
                  <div key={item.id} className="border-t border-neutral-10">
                    {hasChildren ? (
                      <Button raw
                        type="button"
                        onClick={() =>
                          setExpandedMobileSection(isExpanded ? null : item.id)
                        }
                        className="flex w-full items-center justify-between px-1 py-4 text-base font-semibold text-text-primary transition-colors"
                      >
                        <span>{item.label}</span>
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-10">
                          {isExpanded
                            ? <ChevronUpIcon size={14} className="text-text-secondary" />
                            : <ChevronDownIcon size={14} className="text-text-secondary" />
                          }
                        </div>
                      </Button>
                    ) : (
                      <MenuItemAnchor
                        href={href ?? "#"}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex w-full items-center justify-between px-1 py-4 text-base font-semibold text-text-primary transition-colors"
                      >
                        <span>{item.label}</span>
                      </MenuItemAnchor>
                    )}
                    {hasChildren && isExpanded && (
                      <div className="pb-3 pl-2 pr-1 space-y-0.5">
                        {item.children?.map((child) => (
                          <MenuItemAnchor
                            key={child.id}
                            href={getMenuItemHref(child)}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center rounded-lg px-2 py-2.5 text-sm font-medium text-text-secondary transition-colors"
                          >
                            {child.type === "collection" && child.collection
                              ? child.collection.title
                              : child.label}
                          </MenuItemAnchor>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom Section: Pickup/Delivery Toggle and Store Info */}
            <div className="mt-auto px-4 pb-safe">
              <div
                className={`border-t border-neutral-10 py-4 space-y-4 transition-opacity duration-150 ${isStoreSwitchPending ? "opacity-60" : ""
                  }`}
                aria-busy={isStoreSwitchPending}
              >
                {isStoreSwitchPending ? (
                  <span className="sr-only">Updating store location</span>
                ) : null}
                {/* Pickup/Delivery Toggle (mobile menu) */}
                <div className="flex rounded-xl border border-neutral-10 bg-bg-1 p-1">
                  <Button raw
                    type="button"
                    onClick={() => handleModeChange("pickup")}
                    disabled={isStoreSwitchPending}
                    data-no-lift
                    aria-pressed={mode === "pickup"}
                    className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-colors duration-200 ease-out select-none focus:outline-none disabled:cursor-wait disabled:opacity-70 ${mode === "pickup"
                      ? "bg-blue-100 text-constant-white"
                      : "bg-transparent text-text-secondary hover:bg-blue-5 hover:text-blue-100"
                      }`}
                  >
                    Pickup
                  </Button>
                  <Button raw
                    type="button"
                    onClick={() => handleModeChange("delivery")}
                    disabled={isStoreSwitchPending}
                    data-no-lift
                    aria-pressed={mode === "delivery"}
                    className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-colors duration-200 ease-out select-none focus:outline-none disabled:cursor-wait disabled:opacity-70 ${mode === "delivery"
                      ? "bg-blue-100 text-constant-white"
                      : "bg-transparent text-text-secondary hover:bg-blue-5 hover:text-blue-100"
                      }`}
                  >
                    Delivery
                  </Button>
                </div>

                {/* Store Information */}
                {(currentLocation || resolvedStore) &&
                  (() => {
                    const store = currentLocation || resolvedStore;
                    const fullAddress = [
                      store?.address,
                      store?.city,
                      store?.province,
                      store?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(",");

                    return (
                      <div className="rounded-xl border border-neutral-10 bg-constant-white p-3 space-y-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-text-primary">
                              {store?.name ? `${store.name} Dispensary` : "Store"}
                            </span>
                            <span className="rounded-full bg-green-5 px-2 py-0.5 text-md font-semibold text-green-50">
                              Open
                            </span>
                          </div>
                          {fullAddress && (
                            <div className="flex items-start gap-1.5 mt-1.5 text-sm text-text-secondary">
                              <MarkerPinIcon
                                size={13}
                                className="mt-0.5 flex-shrink-0 text-neutral-40"
                              />
                              <span>{fullAddress}</span>
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            if (mode === "pickup") {
                              setNewLocation(null);
                            }
                            deliveryModalOpenedFromPickupToggleRef.current = false;
                            setShowLocationModal(true);
                          }}
                          disabled={isStoreSwitchPending}
                          className="w-full rounded-lg"
                        >
                          {isStoreSwitchPending ? "Updating…" : "Change store"}
                        </Button>
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>
    </>
  );
}

// Main export - client-only wrapper to ensure CartProvider is available
export default function Header({
  stores,
  base,
  searchQuery = "",
  persistentRoot = false,
}: HeaderProps) {
  const persistentHeaderEnabled = usePersistentHeaderEnabled();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (persistentHeaderEnabled && !persistentRoot) {
    return null;
  }

  // Only render HeaderContent after mounting (client-side) to ensure CartProvider is available
  if (!mounted) {
    // SSR placeholder. Reserve the same vertical space as the hydrated header
    // so HeaderContent's mount doesn't push the page down (WPT measured a
    // 121 px CLS culprit attributed to this gap). Heights mirror the real
    // header's rows in HeaderContent: marquee + main row + secondary nav +
    // alert banner.
    return (
      <header className="relative z-30 w-full min-w-0 max-w-full border-b border-neutral-10 bg-constant-white">
        {/* Top marquee bar, reserve the `h-6` (24px) content height so the
            client-rendered marquee doesn't grow the bar after hydration. */}
        <div className="w-full min-w-0 overflow-hidden bg-blue-100 py-1.5 text-constant-white md:py-2">
          <div className="h-6" aria-hidden="true" />
        </div>
        <div className="bg-constant-white border-b border-neutral-10">
          <div className="mx-auto w-full">
            <div className="flex items-center justify-between h-20 md:h-24 gap-2 md:gap-4 py-3 md:py-4 px-4 sm:px-6 lg:px-10">
              <div className="flex items-center gap-2 md:gap-0 flex-shrink-0 h-full">
                <Link href="/" className="flex items-center gap-2 h-full">
                  <Image
                    src="/media/brand/logo.webp"
                    alt="Dank Cannabis"
                    width={109}
                    height={56}
                    className="h-[56px] w-auto"
                  />
                </Link>
              </div>
            </div>
            {/* Mobile search row: 48px field plus its pb-3. */}
            <div className="lg:hidden px-4 pb-3 sm:px-6" aria-hidden="true">
              <div className="h-12 rounded-xl border border-neutral-10" />
            </div>
          </div>
        </div>
        {/* Secondary navigation row, reserve its height (py-3 + ~h-5/h-6
            content) so the categories/search bar doesn't shift content on
            mount. */}
        <div className="bg-constant-white border-b border-neutral-10" aria-hidden="true">
          <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
            <div className="py-3">
              <div className="h-6" />
            </div>
          </div>
        </div>
        {/* Alert banner reserve. The real banner wraps onto ~2 lines on
            desktop so its rendered height exceeds the 36px min, use 56px to
            match the rendered text height (py-1.5 + 2 lines of body-14). */}
        <div className="min-h-[56px] sm:min-h-[56px] bg-transparent" aria-hidden="true" />
      </header>
    );
  }

  return (
    <HeaderContent stores={stores} base={base} searchQuery={searchQuery} />
  );
}
